import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { useTenantStore } from '@/stores/tenantStore';
import { useTenantPath } from '@/hooks/useTenantPath';
import {
  createMenuItem,
  deleteMenuItem,
  exportMenuBulk,
  fetchMenu,
  fetchMenuMetrics,
  fetchMenuTopItems,
  fetchMenuBulkJobs,
  importMenuBulk,
  recordMenuEvent,
  toggleItemEightySix,
  updateMenuItemDetails,
  type FetchMenuResponse,
  type MenuBulkData,
  type MenuBulkImportEnvelope,
  type MenuBulkJobRow,
  type MenuAnalyticsTopItem,
  type MenuCategory,
  type MenuItem,
} from '@/lib/api/menu';
import { normalizeImageUrl, resolveDisplayImageUrl } from '@/lib/images/normalizeImageUrl';
import { uploadMenuImage } from '@/lib/images/uploadMenuImage';
import {
  validateImageUrl,
  IMAGE_URL_GUIDANCE,
  IMAGE_URL_ERROR_TOO_LONG,
  IMAGE_URL_ERROR_DATA_URI,
  IMAGE_URL_ERROR_INVALID,
} from '@/lib/images/validateImageUrl';
import Papa from 'papaparse';
import type { MenuAvailabilityRules, MenuSpiceLevel } from '@/types/menuAvailability';

import { AddCategoryModal } from '../components/AddCategoryModal';
import { ModifierGroupsEditor } from '../components/ModifierGroupsEditor';
import { AvailabilityEditor } from '../components/menu/AvailabilityEditor';
import { useTranslation } from 'react-i18next';
import type { LucideIcon } from 'lucide-react';
import {
  AlertCircle,
  ArrowUpRight,
  BarChart3,
  CheckCircle2,
  ChefHat,
  Clock,
  DollarSign,
  Eye,
  Filter,
  Flame,
  Download,
  Heart,
  Image as ImageIcon,
  Leaf,
  ListChecks,
  Loader2,
  FileSpreadsheet,
  Package,
  Pencil,
  Plus,
  Search,
  Star,
  Tag,
  TrendingDown,
  TrendingUp,
  Upload,
  UploadCloud,
  Users,
  X,
  XCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { BulkImportDialog } from '@/features/restaurant/components/menu/BulkImportDialog';
import { BulkJobHistory } from '@/features/restaurant/components/menu/BulkJobHistory';
const priceFormatter = new Intl.NumberFormat(undefined, {
  style: 'currency',
  currency: 'USD',
});

function formatCurrency(cents: number | undefined): string {
  if (typeof cents !== 'number') return priceFormatter.format(0);
  return priceFormatter.format(cents / 100);
}

const METRICS_WINDOW_DAYS = 30;

const EMPTY_AVAILABILITY_RULES: MenuAvailabilityRules = { dayparts: [], holidays: [], limitedRuns: [] } as const;

const cloneAvailabilityRules = (rules?: MenuAvailabilityRules | null): MenuAvailabilityRules => ({
  timezone: rules?.timezone,
  dayparts: (rules?.dayparts ?? []).map((entry) => ({ ...entry, days: [...entry.days] })),
  holidays: (rules?.holidays ?? []).map((entry) => ({ ...entry })),
  limitedRuns: (rules?.limitedRuns ?? []).map((entry) => ({ ...entry })),
});

const spiceLevelLabels: Record<MenuSpiceLevel, string> = {
  none: 'No spice indicator',
  mild: 'Mild',
  medium: 'Medium',
  hot: 'Hot',
  extra_hot: 'Extra hot',
};

const categoryIconMap: Record<string, LucideIcon> = {
  'main course': ChefHat,
  'main courses': ChefHat,
  entrees: ChefHat,
  appetizers: Leaf,
  starters: Leaf,
  desserts: Heart,
  beverages: Package,
  drinks: Package,
  specials: Star,
  spicy: Flame,
};

const getItemStatus = (item: MenuItem): 'active' | 'inactive' | 'out-of-stock' => {
  const status = item.availability?.status ?? (item.isAvailable ? 'available' : 'inactive');
  if (status === 'temporarily_unavailable') {
    return 'out-of-stock';
  }
  if (status === 'available') {
    return 'active';
  }
  return 'inactive';
};

const useMenuImageUploadCopy = () => {
  const { t } = useTranslation('menu');
  return useMemo(() => {
    const validationMap = new Map<string, string>([
      [IMAGE_URL_ERROR_TOO_LONG, t('imageUpload.validation.tooLong', { defaultValue: IMAGE_URL_ERROR_TOO_LONG })],
      [IMAGE_URL_ERROR_DATA_URI, t('imageUpload.validation.dataUri', { defaultValue: IMAGE_URL_ERROR_DATA_URI })],
      [IMAGE_URL_ERROR_INVALID, t('imageUpload.validation.invalid', { defaultValue: IMAGE_URL_ERROR_INVALID })],
    ]);

    return {
      validationMap,
      guidance: t('imageUpload.guidance', { defaultValue: IMAGE_URL_GUIDANCE }),
      adjustedNotice: t('imageUpload.adjustedNotice', {
        defaultValue:
          'We adjusted the link so it can display correctly for guests. Make sure the original file is shared publicly.',
      }),
      previewAlt: t('imageUpload.previewAlt', { defaultValue: 'Preview' }),
      upload: t('imageUpload.actions.upload', { defaultValue: 'Upload' }),
      uploading: t('imageUpload.actions.uploading', { defaultValue: 'Uploading...' }),
      missingTenant: {
        title: t('imageUpload.missingTenant.title', { defaultValue: 'Select a restaurant first' }),
        description: t('imageUpload.missingTenant.description', {
          defaultValue: 'Choose a restaurant before uploading menu images.',
        }),
      },
      unsupported: {
        title: t('imageUpload.unsupported.title', { defaultValue: 'Unsupported file' }),
        description: t('imageUpload.unsupported.description', {
          defaultValue: 'Please choose an image file (PNG, JPG, GIF).',
        }),
      },
      tooLarge: {
        title: t('imageUpload.tooLarge.title', { defaultValue: 'File too large' }),
        description: t('imageUpload.tooLarge.description', {
          defaultValue: 'Please pick an image smaller than 5 MB.',
        }),
      },
      success: {
        title: t('imageUpload.success.title', { defaultValue: 'Image uploaded' }),
        description: t('imageUpload.success.description', {
          defaultValue: 'We linked the uploaded image to this menu item.',
        }),
      },
      failure: {
        title: t('imageUpload.failure.title', { defaultValue: 'Upload failed' }),
        description: t('imageUpload.failure.description', {
          defaultValue: 'Could not upload image. Try again later.',
        }),
      },
      inProgress: {
        title: t('imageUpload.inProgress.title', { defaultValue: 'Image upload in progress' }),
        description: t('imageUpload.inProgress.description', {
          defaultValue: 'Please wait for the upload to finish before saving.',
        }),
      },
      invalidLink: {
        title: t('imageUpload.invalidLink.title', { defaultValue: 'Invalid image link' }),
      },
    };
  }, [t]);
};

const useQuickEditCopy = () => {
  const { t } = useTranslation('menu');
  return useMemo(
    () => ({
      title: t('quickEdit.title', { defaultValue: 'Edit Menu Item' }),
      description: t('quickEdit.description', {
        defaultValue: 'Update pricing, availability, or category placement.',
      }),
      fields: {
        name: t('quickEdit.fields.name', { defaultValue: 'Name' }),
        description: t('quickEdit.fields.description', { defaultValue: 'Description' }),
        altText: t('quickEdit.fields.altText', { defaultValue: 'Image alt text' }),
        spanishName: t('quickEdit.fields.spanishName', { defaultValue: 'Nombre (Español)' }),
        spanishDescription: t('quickEdit.fields.spanishDescription', { defaultValue: 'Descripción (Español)' }),
        spanishAltText: t('quickEdit.fields.spanishAltText', { defaultValue: 'Alt text (Español)' }),
        price: t('quickEdit.fields.price', { defaultValue: 'Price' }),
        category: t('quickEdit.fields.category', { defaultValue: 'Category' }),
        imageUrl: t('quickEdit.fields.imageUrl', { defaultValue: 'Image URL' }),
      },
      placeholders: {
        category: t('quickEdit.placeholders.category', { defaultValue: 'Select category' }),
        imageUrl: t('quickEdit.placeholders.imageUrl', { defaultValue: 'https://example.com/image.jpg' }),
        altText: t('quickEdit.placeholders.altText', {
          defaultValue: 'Describe the image for accessibility',
        }),
        spanishName: t('quickEdit.placeholders.spanishName', {
          defaultValue: 'Nombre del artículo en español',
        }),
        spanishDescription: t('quickEdit.placeholders.spanishDescription', {
          defaultValue: 'Descripción en español',
        }),
        spanishAltText: t('quickEdit.placeholders.spanishAltText', {
          defaultValue: 'Describe la imagen en español',
        }),
      },
      availability: {
        label: t('quickEdit.availability.label', { defaultValue: 'Available for ordering' }),
        helper: t('quickEdit.availability.helper', { defaultValue: 'Toggle customer visibility' }),
      },
      addons: {
        title: t('quickEdit.addons.title', { defaultValue: 'Add-ons & Customizations' }),
        description: t('quickEdit.addons.description', {
          defaultValue: 'Attach toppings, sides, or upgrades through modifier groups.',
        }),
        managerCta: t('quickEdit.addons.managerCta', { defaultValue: 'Open manager' }),
        addGroup: t('quickEdit.addons.addGroup', { defaultValue: 'Add add-on group' }),
      },
      buttons: {
        cancel: t('quickEdit.buttons.cancel', { defaultValue: 'Cancel' }),
        save: t('quickEdit.buttons.save', { defaultValue: 'Save changes' }),
        removeImage: t('quickEdit.buttons.removeImage', { defaultValue: 'Remove Image' }),
      },
      errors: {
        invalidPrice: t('quickEdit.errors.invalidPrice', {
          defaultValue: 'Enter a valid price greater than zero.',
        }),
        spanishNameRequired: t('quickEdit.errors.spanishNameRequired', {
          defaultValue: 'Spanish name is required.',
        }),
        spanishDescriptionRequired: t('quickEdit.errors.spanishDescriptionRequired', {
          defaultValue: 'Spanish description is required.',
        }),
      },
    }),
    [t],
  );
};

const useAddMenuItemCopy = () => {
  const { t } = useTranslation('menu');
  return useMemo(
    () => ({
      title: t('addItemDialog.title', { defaultValue: 'Add New Menu Item' }),
      sections: {
        basic: t('addItemDialog.sections.basic', { defaultValue: 'Basic Information' }),
        pricing: t('addItemDialog.sections.pricing', { defaultValue: 'Pricing & Timing' }),
        image: t('addItemDialog.sections.image', { defaultValue: 'Image' }),
        availability: t('addItemDialog.sections.availability', { defaultValue: 'Availability & Schedule' }),
        dietary: t('addItemDialog.sections.dietary', { defaultValue: 'Dietary Information' }),
        customTags: t('addItemDialog.sections.customTags', { defaultValue: 'Custom Tags' }),
        allergens: t('addItemDialog.sections.allergens', { defaultValue: 'Allergen Information' }),
        addons: t('addItemDialog.sections.addons', { defaultValue: 'Add-ons & Customizations' }),
        stock: t('addItemDialog.sections.stock', { defaultValue: 'Stock Management' }),
      },
      labels: {
        name: t('addItemDialog.labels.name', { defaultValue: 'Item Name' }),
        description: t('addItemDialog.labels.description', { defaultValue: 'Description' }),
        spanishName: t('addItemDialog.labels.spanishName', { defaultValue: 'Nombre (Español)' }),
        spanishDescription: t('addItemDialog.labels.spanishDescription', { defaultValue: 'Descripción (Español)' }),
        category: t('addItemDialog.labels.category', { defaultValue: 'Category' }),
        status: t('addItemDialog.labels.status', { defaultValue: 'Status' }),
        price: t('addItemDialog.labels.price', { defaultValue: 'Price ($)' }),
        prepTime: t('addItemDialog.labels.prepTime', { defaultValue: 'Prep Time' }),
        calories: t('addItemDialog.labels.calories', { defaultValue: 'Calories' }),
        imageUrl: t('addItemDialog.labels.imageUrl', { defaultValue: 'Image URL' }),
        altText: t('addItemDialog.labels.altText', { defaultValue: 'Image alt text' }),
        spanishAltText: t('addItemDialog.labels.spanishAltText', { defaultValue: 'Alt text (Español)' }),
        spiceLevel: t('addItemDialog.labels.spiceLevel', { defaultValue: 'Spice level' }),
        autoExpire: t('addItemDialog.labels.autoExpire', { defaultValue: 'Auto-expire at (optional)' }),
        soldOutTimer: t('addItemDialog.labels.soldOutTimer', { defaultValue: 'Temporary sold-out timer' }),
        stockQuantity: t('addItemDialog.labels.stockQuantity', { defaultValue: 'Stock Quantity (Optional)' }),
      },
      placeholders: {
        name: t('addItemDialog.placeholders.name', { defaultValue: 'e.g., Truffle Mushroom Pasta' }),
        description: t('addItemDialog.placeholders.description', { defaultValue: 'Describe your dish...' }),
        spanishName: t('addItemDialog.placeholders.spanishName', {
          defaultValue: 'e.g., Pasta de trufa y champiñones',
        }),
        spanishDescription: t('addItemDialog.placeholders.spanishDescription', {
          defaultValue: 'Describe el platillo en español...',
        }),
        category: t('addItemDialog.placeholders.category', { defaultValue: 'Select a category' }),
        price: t('addItemDialog.placeholders.price', { defaultValue: '0.00' }),
        prepTime: t('addItemDialog.placeholders.prepTime', { defaultValue: 'e.g., 15-20 min' }),
        calories: t('addItemDialog.placeholders.calories', { defaultValue: 'e.g., 450' }),
        imageUrl: t('addItemDialog.placeholders.imageUrl', { defaultValue: 'https://example.com/image.jpg' }),
        altText: t('addItemDialog.placeholders.altText', { defaultValue: 'Describe the image for accessibility' }),
        spanishAltText: t('addItemDialog.placeholders.spanishAltText', {
          defaultValue: 'Describe la imagen en español',
        }),
        spiceLevel: t('addItemDialog.placeholders.spiceLevel', { defaultValue: 'Select spice level' }),
        customTag: t('addItemDialog.placeholders.customTag', { defaultValue: 'e.g., Chef Special, Premium' }),
        allergen: t('addItemDialog.placeholders.allergen', { defaultValue: 'e.g., Nuts, Dairy, Gluten' }),
        addonName: t('addItemDialog.placeholders.addonName', { defaultValue: 'Add-on name' }),
        addonPrice: t('addItemDialog.placeholders.addonPrice', { defaultValue: 'Price' }),
        soldOutTimer: t('addItemDialog.placeholders.soldOutTimer', { defaultValue: 'Sold-out timer' }),
        soldOutCustom: t('addItemDialog.placeholders.soldOutCustom', { defaultValue: 'Minutes' }),
        stockQuantity: t('addItemDialog.placeholders.stockQuantity', {
          defaultValue: 'Leave empty for unlimited',
        }),
      },
      statusOptions: {
        active: t('filters.status.active', { defaultValue: 'Active' }),
        inactive: t('filters.status.inactive', { defaultValue: 'Inactive' }),
        outOfStock: t('filters.status.outOfStock', { defaultValue: 'Out of Stock' }),
      },
      soldOutOptions: {
        none: t('addItemDialog.soldOut.none', { defaultValue: 'No timer' }),
        thirty: t('addItemDialog.soldOut.thirty', { defaultValue: '30 minutes' }),
        sixty: t('addItemDialog.soldOut.sixty', { defaultValue: '60 minutes' }),
        oneTwenty: t('addItemDialog.soldOut.oneTwenty', { defaultValue: '120 minutes' }),
        custom: t('addItemDialog.soldOut.custom', { defaultValue: 'Custom duration' }),
      },
      dietaryTags: {
        vegetarian: t('addItemDialog.dietaryTags.vegetarian', { defaultValue: 'Vegetarian' }),
        vegan: t('addItemDialog.dietaryTags.vegan', { defaultValue: 'Vegan' }),
        glutenFree: t('addItemDialog.dietaryTags.glutenFree', { defaultValue: 'Gluten Free' }),
        dairyFree: t('addItemDialog.dietaryTags.dairyFree', { defaultValue: 'Dairy Free' }),
        nutFree: t('addItemDialog.dietaryTags.nutFree', { defaultValue: 'Nut Free' }),
        spicy: t('addItemDialog.dietaryTags.spicy', { defaultValue: 'Spicy' }),
        halal: t('addItemDialog.dietaryTags.halal', { defaultValue: 'Halal' }),
        kosher: t('addItemDialog.dietaryTags.kosher', { defaultValue: 'Kosher' }),
      },
      helpers: {
        stock: t('messages.stockHelp', {
          defaultValue: 'Set a stock quantity to track inventory. Leave empty if not tracking stock.',
        }),
        autoExpire: t('addItemDialog.helpers.autoExpire', {
          defaultValue: 'Automatically deactivate the item at a specific time.',
        }),
        soldOut: t('addItemDialog.helpers.soldOut', {
          defaultValue: 'Temporarily mark the item as sold out with an optional timer.',
        }),
        schedule: t('addItemDialog.helpers.schedule', {
          defaultValue: 'Control when this item is available across guest channels.',
        }),
      },
      buttons: {
        cancel: t('addItemDialog.buttons.cancel', { defaultValue: 'Cancel' }),
        submit: t('addItemDialog.buttons.submit', { defaultValue: 'Add Menu Item' }),
      },
      errors: {
        tenantRequired: t('errors.addItem.tenantRequired', {
          defaultValue: 'Select a restaurant before adding menu items.',
        }),
        nameRequired: t('errors.addItem.nameRequired', { defaultValue: 'Please enter a menu item name.' }),
        spanishNameRequired: t('errors.addItem.spanishNameRequired', {
          defaultValue: 'Please enter the Spanish name.',
        }),
        spanishDescriptionRequired: t('errors.addItem.spanishDescriptionRequired', {
          defaultValue: 'Please enter the Spanish description.',
        }),
        categoryRequired: t('errors.addItem.categoryRequired', { defaultValue: 'Please choose a category.' }),
        priceInvalid: t('errors.addItem.priceInvalid', {
          defaultValue: 'Enter a valid price greater than zero.',
        }),
        uploadPending: t('errors.addItem.uploadPending', {
          defaultValue: 'Please wait for the image upload to finish.',
        }),
        autoExpireInvalid: t('errors.addItem.autoExpireInvalid', {
          defaultValue: 'Select a valid auto-expiration time.',
        }),
        soldOutDurationInvalid: t('errors.addItem.soldOutDurationInvalid', {
          defaultValue: 'Enter a sold-out duration greater than zero.',
        }),
      },
      itemCreatedFallback: t('toasts.itemCreated.descriptionFallback', {
        defaultValue: 'Menu item saved successfully.',
      }),
    }),
    [t],
  );
};

const useMenuPageCopy = () => {
  const { t } = useTranslation('menu');
  const { t: tCommon } = useTranslation('common');
  return useMemo(() => {
    const analytics = {
      totalItemsLabel: t('analytics.cards.totalItems.label', { defaultValue: 'Total Items' }),
      totalItemsTrend: (count: string) =>
        t('analytics.cards.totalItems.trend', { count, defaultValue: `+${count} available` }),
      totalItemsFallback: t('analytics.cards.totalItems.fallback', { defaultValue: 'Awaiting data' }),
      mostViewedLabel: t('analytics.cards.mostViewed.label', { defaultValue: 'Most Viewed' }),
      mostViewedValueFallback: t('analytics.cards.mostViewed.valueFallback', { defaultValue: 'No data yet' }),
      mostViewedTrend: (count: string) =>
        t('analytics.cards.mostViewed.trend', { count, defaultValue: `${count} views` }),
      mostViewedFallback: t('analytics.cards.mostViewed.fallback', { defaultValue: 'Waiting for diners' }),
      viewsLabel: t('analytics.cards.views.label', { defaultValue: 'Menu Views' }),
      viewsTrendPositive: (count: string) =>
        t('analytics.cards.views.trendPositive', { count, defaultValue: `+${count} vs previous` }),
      viewsTrendNegative: (count: string) =>
        t('analytics.cards.views.trendNegative', { count, defaultValue: `${count} vs previous` }),
      viewsTrendFlat: t('analytics.cards.views.trendFlat', { defaultValue: 'Flat vs previous' }),
      conversionLabel: t('analytics.cards.conversion.label', { defaultValue: 'Conversion Rate' }),
      conversionTrend: (count: string) =>
        t('analytics.cards.conversion.trend', { count, defaultValue: `${count} orders` }),
      conversionFallback: t('analytics.cards.conversion.fallback', { defaultValue: 'Waiting for orders' }),
      today: t('analytics.cards.today', {
        defaultValue: tCommon('dates.today', { defaultValue: 'today' }),
      }),
    };

    return {
      header: {
        title: t('metadata.headerTitle', { defaultValue: 'Digital Menu Management' }),
        subtitle: t('metadata.headerSubtitle', {
          defaultValue: 'Manage your digital menu items and track performance',
        }),
      },
      actions: {
        addCategory: t('actions.addCategory', { defaultValue: 'Add Category' }),
        addMenuItem: t('actions.addMenuItem', { defaultValue: 'Add Menu Item' }),
        edit: t('actions.edit', { defaultValue: 'Edit' }),
        view: t('actions.view', { defaultValue: 'View' }),
        removeItem: t('actions.removeItem', { defaultValue: 'Remove item' }),
        addFirstMenuItem: t('actions.addFirstMenuItem', { defaultValue: 'Add First Menu Item' }),
        editCategory: t('actions.editCategory', { defaultValue: 'Edit Category' }),
        toggleAvailability: {
          makeAvailable: t('actions.toggleAvailability.makeAvailable', { defaultValue: 'Mark item available' }),
          makeUnavailable: t('actions.toggleAvailability.makeUnavailable', {
            defaultValue: 'Mark item unavailable',
          }),
        },
      },
      quickFilters: {
        all: t('tabs.all', { defaultValue: 'All Items' }),
      },
      filters: {
        searchPlaceholder: t('filters.searchPlaceholder', { defaultValue: 'Search menu items...' }),
        status: {
          placeholder: t('filters.status.placeholder', { defaultValue: 'Status' }),
          all: t('filters.status.all', { defaultValue: 'All Status' }),
          active: t('filters.status.active', { defaultValue: 'Active' }),
          inactive: t('filters.status.inactive', { defaultValue: 'Inactive' }),
          outOfStock: t('filters.status.outOfStock', { defaultValue: 'Out of Stock' }),
        },
      },
      directory: {
        title: t('directory.title', { defaultValue: 'Menu Directory' }),
        empty: {
          title: t('directory.empty.title', { defaultValue: 'No Menu Items Found' }),
          search: t('directory.empty.search', {
            defaultValue: 'No items match your search criteria. Try adjusting your filters.',
          }),
          getStarted: t('directory.empty.getStarted', {
            defaultValue: 'Get started by adding your first menu item.',
          }),
        },
        noDescription: t('directory.noDescription', { defaultValue: 'No description added yet.' }),
      },
      labels: {
        orders: t('labels.orders', { defaultValue: 'Orders' }),
        views: t('labels.views', { defaultValue: 'Views' }),
        rating: t('labels.rating', { defaultValue: 'Rating' }),
        time: t('labels.time', { defaultValue: 'Time' }),
        price: t('labels.price', { defaultValue: 'Price' }),
        minutes: (value: number | string) =>
          t('labels.minutes', { value, defaultValue: `${value} min` }),
      },
      badges: {
        popular: t('labels.popularBadge', { defaultValue: 'Popular' }),
        outOfStock: t('labels.outOfStockBadge', { defaultValue: 'Out of Stock' }),
        hidden: t('labels.hiddenBadge', { defaultValue: 'Hidden' }),
        active: t('labels.activeBadge', { defaultValue: 'Active' }),
        outShort: t('labels.outShort', { defaultValue: 'Out' }),
      },
      messages: {
        selectTenantToPreview: t('messages.selectTenantToPreview', {
          defaultValue: 'Select a tenant before previewing menu items.',
        }),
      },
      analytics,
    };
  }, [t, tCommon]);
};
interface QuickEditDialogProps {
  open: boolean;
  item: MenuItem | null;
  categories: MenuCategory[];
  tenantSlug?: string;
  timezone?: string | null;
  onClose: () => void;
  onSubmit: (payload: {
    name: string;
    description: string | null;
    priceCents: number;
    taxRateBps?: number | null;
    categoryId: string;
    isActive: boolean;
    imageUrl: string | null;
    availabilityRules: MenuAvailabilityRules;
    autoExpireAt: number | null;
    imageAltText: string | null;
    imageSpiceLevel: MenuSpiceLevel | null;
    soldOutOption: 'none' | '30' | '60' | '120' | 'custom';
    customSoldOutMinutes: string;
    translations: Record<string, { name?: string; description?: string; imageAltText?: string | null }>;
  }) => Promise<void>;
  isSubmitting: boolean;
  onOpenManager?: (itemId: string) => void;
}

const QuickEditDialog = ({ open, item, categories, tenantSlug, timezone, onClose, onSubmit, isSubmitting, onOpenManager }: QuickEditDialogProps) => {
  const { toast } = useToast();
  const imageCopy = useMenuImageUploadCopy();
  const quickEditCopy = useQuickEditCopy();
  const translateImageValidationMessage = useCallback(
    (message: string | null) => {
      if (!message) return null;
      return imageCopy.validationMap.get(message) ?? message;
    },
    [imageCopy.validationMap],
  );
  const [name, setName] = useState('');
  const [description, setDescription] = useState<string>('');
  const [spanishName, setSpanishName] = useState('');
  const [spanishDescription, setSpanishDescription] = useState('');
  const [price, setPrice] = useState('0.00');
  const [categoryId, setCategoryId] = useState<string>('');
  const [isActive, setIsActive] = useState(true);
  const [imageUrl, setImageUrl] = useState('');
  const [imageAltText, setImageAltText] = useState('');
  const [spanishAltText, setSpanishAltText] = useState('');
  const [spiceLevel, setSpiceLevel] = useState<MenuSpiceLevel>('none');
  const [availabilityRules, setAvailabilityRules] = useState<MenuAvailabilityRules>(cloneAvailabilityRules(EMPTY_AVAILABILITY_RULES));
  const [autoExpireAtInput, setAutoExpireAtInput] = useState('');
  const [soldOutOption, setSoldOutOption] = useState<'none' | '30' | '60' | '120' | 'custom'>('none');
  const [customSoldOutMinutes, setCustomSoldOutMinutes] = useState('');
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const rawImageUrl = imageUrl.trim();
  const previewImageUrl = resolveDisplayImageUrl(rawImageUrl);
  const [imageError, setImageError] = useState<string | null>(null);

  const inProgressToastTitle = imageCopy.inProgress.title;
  const inProgressToastDescription = imageCopy.inProgress.description;
  const invalidLinkToastTitle = imageCopy.invalidLink.title;
  const invalidPriceMessage = quickEditCopy.errors.invalidPrice;

  useEffect(() => {
    if (item) {
      setName(item.name);
      setDescription(item.description ?? '');
      setPrice((item.priceCents / 100).toFixed(2));
      setCategoryId(item.categoryId ?? '');
      setIsActive(item.isActive ?? true);
      const nextUrl = item.imageUrl ?? '';
      setImageUrl(nextUrl);
      setImageError(translateImageValidationMessage(validateImageUrl(nextUrl)));
      setImageAltText(item.imageAltText ?? '');
      const esTranslation = item.translations?.es ?? {};
      setSpanishName(typeof esTranslation?.name === 'string' ? esTranslation.name : '');
      setSpanishDescription(typeof esTranslation?.description === 'string' ? String(esTranslation.description) : '');
      setSpanishAltText(typeof esTranslation?.imageAltText === 'string' ? esTranslation.imageAltText : '');
      setSpiceLevel(item.imageSpiceLevel ?? 'none');
      setAvailabilityRules(cloneAvailabilityRules(item.availabilityRules ?? EMPTY_AVAILABILITY_RULES));
      setAutoExpireAtInput(item.autoExpireAt ? new Date(item.autoExpireAt).toISOString().slice(0, 16) : '');
      if (item.availability?.soldOutUntil && item.availability.soldOutUntil > Date.now()) {
        const minutesRemaining = Math.round((item.availability.soldOutUntil - Date.now()) / 60000);
        if ([30, 60, 120].includes(minutesRemaining)) {
          setSoldOutOption(String(minutesRemaining) as '30' | '60' | '120');
          setCustomSoldOutMinutes('');
        } else {
          setSoldOutOption('custom');
          setCustomSoldOutMinutes(String(Math.max(minutesRemaining, 0)));
        }
      } else {
        setSoldOutOption('none');
        setCustomSoldOutMinutes('');
      }
    } else {
      setIsActive(true);
      setImageUrl('');
      setImageError(null);
      setImageAltText('');
      setSpanishName('');
      setSpanishDescription('');
      setSpanishAltText('');
      setSpiceLevel('none');
      setAvailabilityRules(cloneAvailabilityRules(EMPTY_AVAILABILITY_RULES));
      setAutoExpireAtInput('');
      setSoldOutOption('none');
      setCustomSoldOutMinutes('');
    }
  }, [item, translateImageValidationMessage]);

  const handleUploadButtonClick = useCallback(() => {
    if (isUploadingImage) return;
    fileInputRef.current?.click();
  }, [isUploadingImage]);

  const handleImageFileSelected = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) {
        return;
      }

      if (!tenantSlug) {
        toast({
          title: imageCopy.missingTenant.title,
          description: imageCopy.missingTenant.description,
          variant: 'destructive',
        });
        event.target.value = '';
        return;
      }

      if (!file.type.startsWith('image/')) {
        toast({
          title: imageCopy.unsupported.title,
          description: imageCopy.unsupported.description,
          variant: 'destructive',
        });
        event.target.value = '';
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: imageCopy.tooLarge.title,
          description: imageCopy.tooLarge.description,
          variant: 'destructive',
        });
        event.target.value = '';
        return;
      }

      setIsUploadingImage(true);
      try {
        const uploadedUrl = await uploadMenuImage(file, tenantSlug);
        setImageUrl(uploadedUrl);
        setImageError(null);
        toast({
          title: imageCopy.success.title,
          description: imageCopy.success.description,
        });
      } catch (error) {
        console.error('[QuickEditDialog] image upload failed', error);
        toast({
          title: imageCopy.failure.title,
          description:
            error instanceof Error && error.message ? error.message : imageCopy.failure.description,
          variant: 'destructive',
        });
      } finally {
        setIsUploadingImage(false);
        event.target.value = '';
      }
    },
    [tenantSlug, toast, imageCopy],
  );

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      if (!item) return;

      if (isUploadingImage) {
        toast({
          title: inProgressToastTitle,
          description: inProgressToastDescription,
        });
        return;
      }

      const rawValidationMessage = validateImageUrl(rawImageUrl);
      const validationMessage = translateImageValidationMessage(rawValidationMessage);
      setImageError(validationMessage);
      if (validationMessage) {
        toast({
          title: invalidLinkToastTitle,
          description: validationMessage,
          variant: 'destructive',
        });
        return;
      }

      const numericPrice = Number.parseFloat(price);
      if (Number.isNaN(numericPrice) || numericPrice < 0) {
        toast({
          title: invalidPriceMessage,
          variant: 'destructive',
        });
        return;
      }

      const autoExpireAt = autoExpireAtInput ? Date.parse(autoExpireAtInput) : null;
      if (autoExpireAtInput && Number.isNaN(autoExpireAt)) {
        toast({
          title: 'Invalid auto-expire time',
          description: 'Please select a valid future time for auto-expiry.',
          variant: 'destructive',
        });
        return;
      }

      const trimmedSpanishName = spanishName.trim();
      if (!trimmedSpanishName) {
        toast({
          title: quickEditCopy.errors.spanishNameRequired,
          variant: 'destructive',
        });
        return;
      }
      const trimmedSpanishDescription = spanishDescription.trim();
      if (!trimmedSpanishDescription) {
        toast({
          title: quickEditCopy.errors.spanishDescriptionRequired,
          variant: 'destructive',
        });
        return;
      }
      const trimmedSpanishAltText = spanishAltText.trim();

      await onSubmit({
        name: name.trim(),
        description: description.trim().length ? description.trim() : null,
        priceCents: Math.round(numericPrice * 100),
        categoryId: categoryId || item.categoryId || '',
        isActive,
        imageUrl: rawImageUrl ? normalizeImageUrl(rawImageUrl) : null,
        availabilityRules: cloneAvailabilityRules(availabilityRules),
        autoExpireAt: autoExpireAt && Number.isFinite(autoExpireAt) ? autoExpireAt : null,
        imageAltText: imageAltText.trim().length ? imageAltText.trim() : null,
        imageSpiceLevel: spiceLevel === 'none' ? null : spiceLevel,
        soldOutOption,
        customSoldOutMinutes,
        translations: {
          es: {
            name: trimmedSpanishName,
            description: trimmedSpanishDescription,
            ...(trimmedSpanishAltText ? { imageAltText: trimmedSpanishAltText } : {}),
          },
        },
      });
    },
    [
      availabilityRules,
      autoExpireAtInput,
      categoryId,
      customSoldOutMinutes,
      description,
      imageAltText,
      inProgressToastDescription,
      inProgressToastTitle,
      invalidLinkToastTitle,
      invalidPriceMessage,
      isActive,
      item,
      name,
      onSubmit,
      price,
      rawImageUrl,
      soldOutOption,
      spiceLevel,
      spanishName,
      spanishDescription,
      spanishAltText,
      quickEditCopy.errors.spanishNameRequired,
      quickEditCopy.errors.spanishDescriptionRequired,
      isUploadingImage,
      toast,
      translateImageValidationMessage,
    ],
  );

  return (
    <Dialog open={open} onOpenChange={(next) => !next && !isSubmitting && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] p-0 bg-card border-border">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-border">
          <DialogTitle className="text-foreground">{quickEditCopy.title}</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {quickEditCopy.description}
          </DialogDescription>
        </DialogHeader>
        {item ? (
          <ScrollArea className="max-h-[calc(90vh-140px)]">
            <form className="px-6 pb-6 space-y-6" onSubmit={handleSubmit}>
              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="quick-edit-name">
                  {quickEditCopy.fields.name}
                </label>
                <Input
                  id="quick-edit-name"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="quick-edit-name-es">
                  {quickEditCopy.fields.spanishName}
                </label>
                <Input
                  id="quick-edit-name-es"
                  value={spanishName}
                  placeholder={quickEditCopy.placeholders.spanishName}
                  onChange={(event) => setSpanishName(event.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="quick-edit-description">
                  {quickEditCopy.fields.description}
                </label>
                <Textarea
                  id="quick-edit-description"
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="quick-edit-description-es">
                  {quickEditCopy.fields.spanishDescription}
                </label>
                <Textarea
                  id="quick-edit-description-es"
                  value={spanishDescription}
                  placeholder={quickEditCopy.placeholders.spanishDescription}
                  onChange={(event) => setSpanishDescription(event.target.value)}
                  rows={3}
                  required
                />
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <label className="text-sm font-medium" htmlFor="quick-edit-price">
                    {quickEditCopy.fields.price}
                  </label>
                  <Input
                    id="quick-edit-price"
                    value={price}
                    onChange={(event) => setPrice(event.target.value)}
                    type="number"
                    min="0"
                    step="0.01"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium" htmlFor="quick-edit-category">
                    {quickEditCopy.fields.category}
                  </label>
                  <Select value={categoryId} onValueChange={setCategoryId}>
                    <SelectTrigger id="quick-edit-category">
                      <SelectValue placeholder={quickEditCopy.placeholders.category} />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((category) => (
                        <SelectItem key={category.id} value={category.id}>
                          {category.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="quick-edit-image-url">
                  {quickEditCopy.fields.imageUrl}
                </label>
                <div className="flex gap-2">
                  <Input
                    id="quick-edit-image-url"
                    type="url"
                    value={imageUrl}
                    placeholder={quickEditCopy.placeholders.imageUrl}
                    onChange={(event) => {
                      const value = event.target.value;
                      setImageUrl(value);
                      setImageError(translateImageValidationMessage(validateImageUrl(value)));
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleUploadButtonClick}
                    disabled={isUploadingImage}
                    className="shrink-0"
                  >
                    {isUploadingImage ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                    {isUploadingImage ? imageCopy.uploading : imageCopy.upload}
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleImageFileSelected}
                  />
                </div>
                {previewImageUrl && previewImageUrl !== rawImageUrl ? (
                  <p className="text-xs text-muted-foreground">
                    {imageCopy.adjustedNotice}
                  </p>
                ) : null}
                {imageError ? (
                  <p className="text-xs text-destructive">{imageError}</p>
                ) : (
                  <p className="text-xs text-muted-foreground">{imageCopy.guidance}</p>
                )}
                {previewImageUrl ? (
                  <div className="p-2 bg-muted/50 rounded-lg border border-border">
                    <img src={previewImageUrl} alt={imageCopy.previewAlt} className="w-full h-40 object-cover rounded-md" />
                  </div>
                ) : null}
                {imageUrl ? (
                  <Button
                    type="button"
                    variant="ghost"
                    className="text-destructive hover:text-destructive/80 hover:bg-destructive/10 w-fit"
                    onClick={() => setImageUrl('')}
                    disabled={isUploadingImage}
                  >
                    {quickEditCopy.buttons.removeImage}
                  </Button>
                ) : null}
            </div>
            <Separator />
            <div className="space-y-2">
              <Label className="text-sm font-medium" htmlFor="quick-edit-alt-text">
                {quickEditCopy.fields.altText}
              </Label>
              <Input
                id="quick-edit-alt-text"
                value={imageAltText}
                onChange={(event) => setImageAltText(event.target.value)}
                placeholder={quickEditCopy.placeholders.altText}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium" htmlFor="quick-edit-alt-text-es">
                {quickEditCopy.fields.spanishAltText}
              </Label>
              <Input
                id="quick-edit-alt-text-es"
                value={spanishAltText}
                onChange={(event) => setSpanishAltText(event.target.value)}
                placeholder={quickEditCopy.placeholders.spanishAltText}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium" htmlFor="quick-edit-spice">Spice level</Label>
              <Select value={spiceLevel} onValueChange={(value) => setSpiceLevel(value as MenuSpiceLevel)}>
                <SelectTrigger id="quick-edit-spice">
                  <SelectValue placeholder="Select spice level" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(spiceLevelLabels).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Separator />
            <div className="space-y-3 rounded-md border border-border/60 bg-muted/30 px-3 py-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">{quickEditCopy.availability.label}</p>
                  <p className="text-xs text-muted-foreground">{quickEditCopy.availability.helper}</p>
                </div>
                <Switch id="quick-edit-active" checked={isActive} onCheckedChange={setIsActive} />
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <div className="space-y-1">
                  <Label className="text-xs font-medium" htmlFor="quick-edit-sold-out">Temporary sold-out</Label>
                  <Select value={soldOutOption} onValueChange={(value) => setSoldOutOption(value as 'none' | '30' | '60' | '120' | 'custom')}>
                    <SelectTrigger id="quick-edit-sold-out">
                      <SelectValue placeholder="Sold-out timer" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No timer</SelectItem>
                      <SelectItem value="30">30 minutes</SelectItem>
                      <SelectItem value="60">60 minutes</SelectItem>
                      <SelectItem value="120">120 minutes</SelectItem>
                      <SelectItem value="custom">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                  {soldOutOption === 'custom' ? (
                    <Input
                      id="quick-edit-sold-out-custom"
                      type="number"
                      min={5}
                      className="mt-1"
                      value={customSoldOutMinutes}
                      onChange={(event) => setCustomSoldOutMinutes(event.target.value)}
                      placeholder="Minutes"
                    />
                  ) : null}
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-medium" htmlFor="quick-edit-auto-expire">Auto-expire at</Label>
                  <Input
                    id="quick-edit-auto-expire"
                    type="datetime-local"
                    value={autoExpireAtInput}
                    onChange={(event) => setAutoExpireAtInput(event.target.value)}
                  />
                </div>
              </div>
              {item.availability ? (
                <p className="text-xs text-muted-foreground">
                  Status:{' '}
                  <span className="font-medium capitalize">{item.availability.status.replace('_', ' ')}</span>
                  {item.availability.nextAvailableAt
                    ? ` • Next available ${new Date(item.availability.nextAvailableAt).toLocaleString()}`
                    : ''}
                </p>
              ) : null}
            </div>
            <Separator />
            <div className="space-y-2">
              <p className="text-sm font-medium">Schedule</p>
              <p className="text-xs text-muted-foreground">Manage dayparts, holidays, and limited-run windows. Times use {timezone ?? 'restaurant timezone'}.</p>
              <AvailabilityEditor value={availabilityRules} onChange={setAvailabilityRules} timezone={timezone} />
            </div>
            <Separator />
            <ModifierGroupsEditor
              itemId={item.id}
              tenantSlug={tenantSlug}
              isOpen={open}
              title={quickEditCopy.addons.title}
              subtitle={quickEditCopy.addons.description}
              addButtonLabel={quickEditCopy.addons.addGroup}
              headerAddon={
                item && onOpenManager ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground hover:text-foreground"
                    onClick={() => onOpenManager(item.id)}
                  >
                    <ListChecks className="mr-2 h-4 w-4" />
                    {quickEditCopy.addons.managerCta}
                  </Button>
                ) : null
              }
            />
            <Separator />
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
              <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
                {quickEditCopy.buttons.cancel}
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {quickEditCopy.buttons.save}
              </Button>
            </div>
            </form>
          </ScrollArea>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

interface ModifierGroupsManagerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenantSlug?: string;
  items: MenuItem[];
  itemId: string;
  onSelectItem: (itemId: string) => void;
}

const ModifierGroupsManagerDialog = ({
  open,
  onOpenChange,
  tenantSlug,
  items,
  itemId,
  onSelectItem,
}: ModifierGroupsManagerDialogProps) => {
  const selectedItem = items.find((item) => item.id === itemId) ?? null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Modifier Group Manager</DialogTitle>
          <DialogDescription>
            Organize modifier groups, variant stacking, and allergen tags with drag-and-drop sorting and bulk actions.
          </DialogDescription>
        </DialogHeader>
        {items.length === 0 ? (
          <div className="rounded-md border border-dashed border-muted-foreground/40 bg-muted/30 p-6 text-sm text-muted-foreground">
            Add a menu item to manage modifier groups.
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div className="flex-1 space-y-1">
                <Label htmlFor="modifier-manager-item">Menu item</Label>
                <Select value={itemId} onValueChange={onSelectItem}>
                  <SelectTrigger id="modifier-manager-item" className="w-full sm:w-80">
                    <SelectValue placeholder="Choose a menu item" />
                  </SelectTrigger>
                  <SelectContent>
                    {items.map((item) => (
                      <SelectItem key={item.id} value={item.id}>
                        {item.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {selectedItem ? (
                <div className="text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">{formatCurrency(selectedItem.priceCents)}</span>
                  {selectedItem.isEightySixed ? ' · Eighty-sixed' : ''}
                  {selectedItem.isAvailable === false ? ' · Hidden' : ''}
                </div>
              ) : null}
            </div>
            {itemId ? (
              <ModifierGroupsEditor
                itemId={itemId}
                tenantSlug={tenantSlug}
                isOpen={open}
                variant="manager"
              />
            ) : (
              <div className="rounded-md border border-dashed border-muted-foreground/40 bg-muted/30 p-6 text-sm text-muted-foreground">
                Select a menu item to manage its modifier groups.
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

interface AddMenuItemDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenantSlug?: string;
  categories: MenuCategory[];
  defaultCategoryId?: string;
  timezone?: string | null;
}

const AddMenuItemDialog = ({ open, onOpenChange, tenantSlug, categories, defaultCategoryId, timezone }: AddMenuItemDialogProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { t } = useTranslation('menu');
  const addItemCopy = useAddMenuItemCopy();
  const imageCopy = useMenuImageUploadCopy();
  const translateImageValidationMessage = useCallback(
    (message: string | null) => {
      if (!message) return null;
      return imageCopy.validationMap.get(message) ?? message;
    },
    [imageCopy.validationMap],
  );
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    categoryId: defaultCategoryId ?? '',
    price: '',
    prepTime: '',
    calories: '',
    status: 'active' as 'active' | 'inactive' | 'out-of-stock',
    imageUrl: '',
    stockQuantity: '',
  });
  const [spanishForm, setSpanishForm] = useState({
    name: '',
    description: '',
    imageAltText: '',
  });
  const [dietaryTags, setDietaryTags] = useState({
    vegetarian: false,
    vegan: false,
    glutenFree: false,
    dairyFree: false,
    nutFree: false,
    spicy: false,
    halal: false,
    kosher: false,
  });
  const [customTags, setCustomTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState('');
  const [allergens, setAllergens] = useState<string[]>([]);
  const [newAllergen, setNewAllergen] = useState('');
  const [addons, setAddons] = useState<Array<{ name: string; price: string }>>([]);
  const [newAddon, setNewAddon] = useState({ name: '', price: '' });
  const [imageAltText, setImageAltText] = useState('');
  const [spiceLevel, setSpiceLevel] = useState<MenuSpiceLevel>('none');
  const [availabilityRules, setAvailabilityRules] = useState<MenuAvailabilityRules>(cloneAvailabilityRules(EMPTY_AVAILABILITY_RULES));
  const [autoExpireAtInput, setAutoExpireAtInput] = useState('');
  const [soldOutOption, setSoldOutOption] = useState<'none' | '30' | '60' | '120' | 'custom'>('none');
  const [customSoldOutMinutes, setCustomSoldOutMinutes] = useState('');
  const rawImageUrlInput = formData.imageUrl.trim();
  const previewImageUrl = resolveDisplayImageUrl(rawImageUrlInput);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);

  useEffect(() => {
    if (open && defaultCategoryId) {
      setFormData((previous) => ({ ...previous, categoryId: defaultCategoryId }));
    }
  }, [defaultCategoryId, open]);

  const resetForm = useCallback(() => {
    setFormData({
      name: '',
      description: '',
      categoryId: defaultCategoryId ?? '',
      price: '',
      prepTime: '',
      calories: '',
      status: 'active',
      imageUrl: '',
      stockQuantity: '',
    });
    setSpanishForm({
      name: '',
      description: '',
      imageAltText: '',
    });
    setDietaryTags({
      vegetarian: false,
      vegan: false,
      glutenFree: false,
      dairyFree: false,
      nutFree: false,
      spicy: false,
      halal: false,
      kosher: false,
    });
    setCustomTags([]);
    setAllergens([]);
    setAddons([]);
    setNewTag('');
    setNewAllergen('');
    setNewAddon({ name: '', price: '' });
    setImageAltText('');
    setSpiceLevel('none');
    setAvailabilityRules(cloneAvailabilityRules(EMPTY_AVAILABILITY_RULES));
    setAutoExpireAtInput('');
    setSoldOutOption('none');
    setCustomSoldOutMinutes('');
    setImageError(null);
  }, [defaultCategoryId]);

  const handleUploadButtonClick = useCallback(() => {
    if (isUploadingImage) {
      return;
    }
    fileInputRef.current?.click();
  }, [isUploadingImage]);

  const handleImageFileSelected = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) {
        return;
      }

      if (!tenantSlug) {
        toast({
          title: imageCopy.missingTenant.title,
          description: imageCopy.missingTenant.description,
          variant: 'destructive',
        });
        event.target.value = '';
        return;
      }

      if (!file.type.startsWith('image/')) {
        toast({
          title: imageCopy.unsupported.title,
          description: imageCopy.unsupported.description,
          variant: 'destructive',
        });
        event.target.value = '';
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: imageCopy.tooLarge.title,
          description: imageCopy.tooLarge.description,
          variant: 'destructive',
        });
        event.target.value = '';
        return;
      }

      setIsUploadingImage(true);
      try {
        const uploadedUrl = await uploadMenuImage(file, tenantSlug);
        setFormData((previous) => ({ ...previous, imageUrl: uploadedUrl }));
        setImageError(null);
        toast({
          title: imageCopy.success.title,
          description: imageCopy.success.description,
        });
      } catch (error) {
        console.error('[MenuPage] image upload failed', error);
        toast({
          title: imageCopy.failure.title,
          description:
            error instanceof Error && error.message ? error.message : imageCopy.failure.description,
          variant: 'destructive',
        });
      } finally {
        setIsUploadingImage(false);
        event.target.value = '';
      }
    },
    [tenantSlug, toast, imageCopy],
  );

  const createItemMutation = useMutation({
    mutationFn: async () => {
      if (!tenantSlug) {
        throw new Error(addItemCopy.errors.tenantRequired);
      }
      if (!formData.name.trim()) {
        throw new Error(addItemCopy.errors.nameRequired);
      }
      if (!formData.categoryId) {
        throw new Error(addItemCopy.errors.categoryRequired);
      }
      const priceValue = Number.parseFloat(formData.price);
      if (Number.isNaN(priceValue) || priceValue <= 0) {
        throw new Error(addItemCopy.errors.priceInvalid);
      }

      if (isUploadingImage) {
        throw new Error(addItemCopy.errors.uploadPending);
      }

      const rawValidationMessage = validateImageUrl(rawImageUrlInput);
      const validationMessage = translateImageValidationMessage(rawValidationMessage);
      setImageError(validationMessage);
      if (validationMessage) {
        throw new Error(validationMessage);
      }

      const rawImageUrl = formData.imageUrl.trim();
      const normalizedImageUrl = rawImageUrl ? normalizeImageUrl(rawImageUrl) : undefined;

      const autoExpireAt = autoExpireAtInput ? Date.parse(autoExpireAtInput) : null;
      if (autoExpireAtInput && Number.isNaN(autoExpireAt)) {
        throw new Error(addItemCopy.errors.autoExpireInvalid);
      }

      const availabilityRulesPayload = cloneAvailabilityRules(availabilityRules);
      const shouldApplySoldOut =
        formData.status !== 'inactive' && (formData.status === 'out-of-stock' || soldOutOption !== 'none');
      const spanishName = spanishForm.name.trim();
      if (!spanishName) {
        throw new Error(addItemCopy.errors.spanishNameRequired);
      }
      const spanishDescription = spanishForm.description.trim();
      if (!spanishDescription) {
        throw new Error(addItemCopy.errors.spanishDescriptionRequired);
      }
      const spanishImageAltText = spanishForm.imageAltText.trim();
      const translationsPayload = {
        es: {
          name: spanishName,
          description: spanishDescription,
          ...(spanishImageAltText.length > 0 ? { imageAltText: spanishImageAltText } : {}),
        },
      };

      let soldOutDurationMinutes: number | null | undefined;
      if (shouldApplySoldOut) {
        if (soldOutOption === 'custom') {
          const parsed = Number.parseInt(customSoldOutMinutes, 10);
          if (!Number.isFinite(parsed) || parsed <= 0) {
            throw new Error(addItemCopy.errors.soldOutDurationInvalid);
          }
          soldOutDurationMinutes = parsed;
        } else if (soldOutOption !== 'none') {
          soldOutDurationMinutes = Number.parseInt(soldOutOption, 10);
        } else {
          soldOutDurationMinutes = null;
        }
      }

      const created = await createMenuItem(tenantSlug, {
        name: formData.name.trim(),
        description: formData.description.trim(),
        categoryId: formData.categoryId,
        priceCents: Math.round(priceValue * 100),
        imageUrl: normalizedImageUrl,
        isActive: formData.status !== 'inactive',
        availabilityRules: availabilityRulesPayload,
        autoExpireAt: autoExpireAt && Number.isFinite(autoExpireAt) ? autoExpireAt : null,
        imageAltText: imageAltText.trim().length ? imageAltText.trim() : null,
        imageSpiceLevel: spiceLevel === 'none' ? null : spiceLevel,
        translations: translationsPayload,
      });

      if (!created) {
        return null;
      }

      const followUps: Promise<unknown>[] = [];
      if (shouldApplySoldOut) {
        const soldOutPayload: Record<string, unknown> = { isEightySixed: true };
        if (soldOutDurationMinutes === null) {
          soldOutPayload.soldOutUntil = null;
        } else if (typeof soldOutDurationMinutes === 'number') {
          soldOutPayload.soldOutDurationMinutes = soldOutDurationMinutes;
        }
        followUps.push(toggleItemEightySix(tenantSlug, created.id, soldOutPayload));
      } else if (formData.status !== 'inactive') {
        followUps.push(toggleItemEightySix(tenantSlug, created.id, { isEightySixed: false, soldOutUntil: null }));
      }

      if (followUps.length) {
        await Promise.allSettled(followUps);
      }

      return created;
  },
  onSuccess: (item) => {
    queryClient.invalidateQueries({ queryKey: ['menu'] });
    queryClient.invalidateQueries({ queryKey: ['menuMetrics'] });
    queryClient.invalidateQueries({ queryKey: ['menuTopItems'] });
    toast({
        title: t('toasts.itemCreated.title'),
        description: item
          ? t('toasts.itemCreated.description', { name: item.name })
          : addItemCopy.itemCreatedFallback,
    });
      resetForm();
      onOpenChange(false);
  },
  onError: (error) => {
      const message =
        error instanceof Error && error.message
          ? error.message
          : t('toasts.itemCreateError.description');
      toast({
        title: t('toasts.itemCreateError.title'),
        description: message,
        variant: 'destructive',
      });
  },
});

  const handleSubmit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      if (!createItemMutation.isPending) {
        createItemMutation.mutate();
      }
    },
    [createItemMutation],
  );

  const handleDietaryToggle = useCallback((tag: keyof typeof dietaryTags) => {
    setDietaryTags((previous) => ({ ...previous, [tag]: !previous[tag] }));
  }, []);

  const addCustomTag = useCallback(() => {
    const value = newTag.trim();
    if (value && !customTags.includes(value)) {
      setCustomTags((previous) => [...previous, value]);
    }
    setNewTag('');
  }, [customTags, newTag]);

  const removeCustomTag = useCallback((tag: string) => {
    setCustomTags((previous) => previous.filter((value) => value !== tag));
  }, []);

  const addAllergen = useCallback(() => {
    const value = newAllergen.trim();
    if (value && !allergens.includes(value)) {
      setAllergens((previous) => [...previous, value]);
    }
    setNewAllergen('');
  }, [allergens, newAllergen]);

  const removeAllergen = useCallback((value: string) => {
    setAllergens((previous) => previous.filter((entry) => entry !== value));
  }, []);

  const addAddonOption = useCallback(() => {
    if (newAddon.name.trim() && newAddon.price.trim()) {
      setAddons((previous) => [...previous, { ...newAddon }]);
      setNewAddon({ name: '', price: '' });
    }
  }, [newAddon]);

  const removeAddon = useCallback((index: number) => {
    setAddons((previous) => previous.filter((_, position) => position !== index));
  }, []);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          resetForm();
        }
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-3xl max-h-[90vh] p-0 bg-card border-border">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 rounded-xl border border-emerald-500/20">
              <ChefHat className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <DialogTitle className="text-foreground">{addItemCopy.title}</DialogTitle>
          </div>
        </DialogHeader>

        <ScrollArea className="max-h-[calc(90vh-140px)]">
          <form onSubmit={handleSubmit} className="px-6 pb-6 space-y-6">
            <section className="space-y-4">
              <div className="flex items-center gap-2 pb-2 border-b border-border">
                <Package className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-foreground">{addItemCopy.sections.basic}</h3>
              </div>
              <div className="grid gap-4">
                <div className="space-y-2">
                  <Label htmlFor="menu-item-name" className="text-foreground">
                    {addItemCopy.labels.name}{' '}
                    <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="menu-item-name"
                    placeholder={addItemCopy.placeholders.name}
                    value={formData.name}
                    onChange={(event) => setFormData((previous) => ({ ...previous, name: event.target.value }))}
                    className="bg-input-background border-border text-foreground"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="menu-item-name-es" className="text-foreground">
                    {addItemCopy.labels.spanishName} <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="menu-item-name-es"
                    placeholder={addItemCopy.placeholders.spanishName}
                    value={spanishForm.name}
                    onChange={(event) => setSpanishForm((previous) => ({ ...previous, name: event.target.value }))}
                    className="bg-input-background border-border text-foreground"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="menu-item-description" className="text-foreground">
                    {addItemCopy.labels.description}
                  </Label>
                  <Textarea
                    id="menu-item-description"
                    placeholder={addItemCopy.placeholders.description}
                    value={formData.description}
                    onChange={(event) => setFormData((previous) => ({ ...previous, description: event.target.value }))}
                    className="bg-input-background border-border text-foreground min-h-20 resize-none"
                    rows={3}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="menu-item-description-es" className="text-foreground">
                    {addItemCopy.labels.spanishDescription} <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    id="menu-item-description-es"
                    placeholder={addItemCopy.placeholders.spanishDescription}
                    value={spanishForm.description}
                    onChange={(event) => setSpanishForm((previous) => ({ ...previous, description: event.target.value }))}
                    className="bg-input-background border-border text-foreground min-h-20 resize-none"
                    rows={3}
                    required
                  />
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="menu-item-category" className="text-foreground">
                      {addItemCopy.labels.category}{' '}
                      <span className="text-red-500">*</span>
                    </Label>
                    <Select
                      value={formData.categoryId}
                      onValueChange={(value) => setFormData((previous) => ({ ...previous, categoryId: value }))}
                    >
                      <SelectTrigger id="menu-item-category" className="bg-input-background border-border text-foreground">
                        <SelectValue placeholder={addItemCopy.placeholders.category} />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((category) => (
                          <SelectItem key={category.id} value={category.id}>
                            {category.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="menu-item-status" className="text-foreground">
                      {addItemCopy.labels.status}
                    </Label>
                    <Select
                      value={formData.status}
                      onValueChange={(value: 'active' | 'inactive' | 'out-of-stock') =>
                        setFormData((previous) => ({ ...previous, status: value }))
                      }
                    >
                      <SelectTrigger id="menu-item-status" className="bg-input-background border-border text-foreground">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">{addItemCopy.statusOptions.active}</SelectItem>
                        <SelectItem value="inactive">{addItemCopy.statusOptions.inactive}</SelectItem>
                        <SelectItem value="out-of-stock">{addItemCopy.statusOptions.outOfStock}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </section>

            <section className="space-y-4">
              <div className="flex items-center gap-2 pb-2 border-b border-border">
                <DollarSign className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-foreground">{addItemCopy.sections.pricing}</h3>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="menu-item-price" className="text-foreground">
                    {addItemCopy.labels.price}{' '}
                    <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="menu-item-price"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder={addItemCopy.placeholders.price}
                    value={formData.price}
                    onChange={(event) => setFormData((previous) => ({ ...previous, price: event.target.value }))}
                    className="bg-input-background border-border text-foreground"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="menu-item-prep" className="text-foreground">
                    {addItemCopy.labels.prepTime}
                  </Label>
                  <Input
                    id="menu-item-prep"
                    placeholder={addItemCopy.placeholders.prepTime}
                    value={formData.prepTime}
                    onChange={(event) => setFormData((previous) => ({ ...previous, prepTime: event.target.value }))}
                    className="bg-input-background border-border text-foreground"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="menu-item-calories" className="text-foreground">
                    {addItemCopy.labels.calories}
                  </Label>
                  <Input
                    id="menu-item-calories"
                    type="number"
                    placeholder={addItemCopy.placeholders.calories}
                    value={formData.calories}
                    onChange={(event) => setFormData((previous) => ({ ...previous, calories: event.target.value }))}
                    className="bg-input-background border-border text-foreground"
                  />
                </div>
              </div>
            </section>

            <section className="space-y-4">
              <div className="flex items-center gap-2 pb-2 border-b border-border">
                <ImageIcon className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-foreground">{addItemCopy.sections.image}</h3>
              </div>
              <div className="space-y-2">
                <Label htmlFor="menu-item-image" className="text-foreground">
                  {addItemCopy.labels.imageUrl}
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="menu-item-image"
                    type="url"
                    placeholder={addItemCopy.placeholders.imageUrl}
                    value={formData.imageUrl}
                    onChange={(event) => {
                      const value = event.target.value;
                      setFormData((previous) => ({ ...previous, imageUrl: value }));
                      setImageError(translateImageValidationMessage(validateImageUrl(value)));
                    }}
                    className="bg-input-background border-border text-foreground"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="bg-card hover:bg-accent border-border text-foreground"
                    onClick={handleUploadButtonClick}
                    disabled={isUploadingImage}
                  >
                    {isUploadingImage ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4 mr-2" />
                    )}
                    {isUploadingImage ? imageCopy.uploading : imageCopy.upload}
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleImageFileSelected}
                  />
                </div>
                {previewImageUrl && previewImageUrl !== rawImageUrlInput ? (
                  <p className="text-xs text-muted-foreground">
                    {imageCopy.adjustedNotice}
                  </p>
                ) : null}
                {imageError ? (
                  <p className="text-xs text-destructive">{imageError}</p>
                ) : (
                  <p className="text-xs text-muted-foreground">{imageCopy.guidance}</p>
                )}
                {previewImageUrl ? (
                  <div className="mt-3 p-2 bg-muted/50 rounded-lg border border-border">
                    <img
                      src={previewImageUrl}
                      alt={imageCopy.previewAlt}
                      className="w-full h-48 object-cover rounded-lg"
                      onError={(event) => {
                        (event.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </div>
                ) : null}
                <div className="grid gap-3 md:grid-cols-2 pt-4 border-t border-border/60">
                  <div className="space-y-2">
                    <Label htmlFor="menu-item-alt-text" className="text-sm font-medium">
                      {addItemCopy.labels.altText}
                    </Label>
                    <Input
                      id="menu-item-alt-text"
                      value={imageAltText}
                      onChange={(event) => setImageAltText(event.target.value)}
                      placeholder={addItemCopy.placeholders.altText}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="menu-item-alt-text-es" className="text-sm font-medium">
                      {addItemCopy.labels.spanishAltText}
                    </Label>
                    <Input
                      id="menu-item-alt-text-es"
                      value={spanishForm.imageAltText}
                      onChange={(event) =>
                        setSpanishForm((previous) => ({ ...previous, imageAltText: event.target.value }))
                      }
                      placeholder={addItemCopy.placeholders.spanishAltText}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="menu-item-spice" className="text-sm font-medium">
                      {addItemCopy.labels.spiceLevel}
                    </Label>
                    <Select value={spiceLevel} onValueChange={(value) => setSpiceLevel(value as MenuSpiceLevel)}>
                      <SelectTrigger id="menu-item-spice" className="bg-input-background border-border text-foreground">
                        <SelectValue placeholder={addItemCopy.placeholders.spiceLevel} />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(spiceLevelLabels).map(([value, label]) => (
                          <SelectItem key={value} value={value}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </section>

            <section className="space-y-4">
              <div className="flex items-center gap-2 pb-2 border-b border-border">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-foreground">{addItemCopy.sections.availability}</h3>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="menu-item-auto-expire" className="text-sm font-medium">
                    {addItemCopy.labels.autoExpire}
                  </Label>
                  <Input
                    id="menu-item-auto-expire"
                    type="datetime-local"
                    value={autoExpireAtInput}
                    onChange={(event) => setAutoExpireAtInput(event.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">{addItemCopy.helpers.autoExpire}</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="menu-item-sold-out" className="text-sm font-medium">
                    {addItemCopy.labels.soldOutTimer}
                  </Label>
                  <Select
                    value={soldOutOption}
                    onValueChange={(value) => setSoldOutOption(value as 'none' | '30' | '60' | '120' | 'custom')}
                  >
                    <SelectTrigger id="menu-item-sold-out" className="bg-input-background border-border text-foreground">
                      <SelectValue placeholder={addItemCopy.placeholders.soldOutTimer} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{addItemCopy.soldOutOptions.none}</SelectItem>
                      <SelectItem value="30">{addItemCopy.soldOutOptions.thirty}</SelectItem>
                      <SelectItem value="60">{addItemCopy.soldOutOptions.sixty}</SelectItem>
                      <SelectItem value="120">{addItemCopy.soldOutOptions.oneTwenty}</SelectItem>
                      <SelectItem value="custom">{addItemCopy.soldOutOptions.custom}</SelectItem>
                    </SelectContent>
                  </Select>
                  {soldOutOption === 'custom' ? (
                    <Input
                      id="menu-item-sold-out-custom"
                      type="number"
                      min={5}
                      value={customSoldOutMinutes}
                      onChange={(event) => setCustomSoldOutMinutes(event.target.value)}
                      placeholder={addItemCopy.placeholders.soldOutCustom}
                    />
                  ) : null}
                  <p className="text-xs text-muted-foreground">{addItemCopy.helpers.soldOut}</p>
                </div>
              </div>
              <div className="space-y-2">
                <AvailabilityEditor value={availabilityRules} onChange={setAvailabilityRules} timezone={timezone} />
                <p className="text-xs text-muted-foreground">{addItemCopy.helpers.schedule}</p>
              </div>
            </section>

            <section className="space-y-4">
              <div className="flex items-center gap-2 pb-2 border-b border-border">
                <Leaf className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-foreground">{addItemCopy.sections.dietary}</h3>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {(Object.keys(dietaryTags) as Array<keyof typeof dietaryTags>).map((tag) => {
                  const displayName = addItemCopy.dietaryTags[tag];
                  return (
                    <div
                      key={tag}
                      className="flex items-center justify-between gap-3 p-3 bg-muted/50 rounded-lg border border-border"
                    >
                      <Label htmlFor={`dietary-${tag}`} className="text-sm text-foreground cursor-pointer truncate flex-1">
                        {displayName}
                      </Label>
                      <Switch
                        id={`dietary-${tag}`}
                        checked={dietaryTags[tag]}
                        onCheckedChange={() => handleDietaryToggle(tag)}
                        className="flex-shrink-0"
                      />
                    </div>
                  );
                })}
              </div>
            </section>

            <section className="space-y-4">
              <div className="flex items-center gap-2 pb-2 border-b border-border">
                <Tag className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-foreground">{addItemCopy.sections.customTags}</h3>
              </div>
              <div className="space-y-3">
                <div className="flex gap-2">
                  <Input
                    placeholder={addItemCopy.placeholders.customTag}
                    value={newTag}
                    onChange={(event) => setNewTag(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        addCustomTag();
                      }
                    }}
                    className="bg-input-background border-border text-foreground"
                  />
                  <Button
                    type="button"
                    onClick={addCustomTag}
                    variant="outline"
                    className="bg-emerald-500/10 hover:bg-emerald-500/20 border-emerald-500/20 text-emerald-700 dark:text-emerald-400"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {customTags.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {customTags.map((tag) => (
                      <Badge
                        key={tag}
                        variant="secondary"
                        className="bg-muted text-foreground border border-border px-3 py-1.5"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => removeCustomTag(tag)}
                          className="ml-2 hover:text-destructive"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                ) : null}
              </div>
            </section>

            <section className="space-y-4">
              <div className="flex items-center gap-2 pb-2 border-b border-border">
                <AlertCircle className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-foreground">{addItemCopy.sections.allergens}</h3>
              </div>
              <div className="space-y-3">
                <div className="flex gap-2">
                  <Input
                    placeholder={addItemCopy.placeholders.allergen}
                    value={newAllergen}
                    onChange={(event) => setNewAllergen(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        addAllergen();
                      }
                    }}
                    className="bg-input-background border-border text-foreground"
                  />
                  <Button
                    type="button"
                    onClick={addAllergen}
                    variant="outline"
                    className="bg-orange-500/10 hover:bg-orange-500/20 border-orange-500/20 text-orange-700 dark:text-orange-400"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {allergens.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {allergens.map((allergen) => (
                      <Badge
                        key={allergen}
                        variant="secondary"
                        className="bg-orange-500/10 text-orange-700 dark:text-orange-400 border border-orange-500/20 px-3 py-1.5"
                      >
                        {allergen}
                        <button
                          type="button"
                          onClick={() => removeAllergen(allergen)}
                          className="ml-2 hover:text-destructive"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                ) : null}
              </div>
            </section>

            <section className="space-y-4">
              <div className="flex items-center gap-2 pb-2 border-b border-border">
                <Plus className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-foreground">{addItemCopy.sections.addons}</h3>
              </div>
              <div className="space-y-3">
                <div className="flex gap-2">
                  <Input
                    placeholder={addItemCopy.placeholders.addonName}
                    value={newAddon.name}
                    onChange={(event) => setNewAddon((previous) => ({ ...previous, name: event.target.value }))}
                    className="bg-input-background border-border text-foreground flex-1"
                  />
                  <Input
                    type="number"
                    step="0.01"
                    placeholder={addItemCopy.placeholders.addonPrice}
                    value={newAddon.price}
                    onChange={(event) => setNewAddon((previous) => ({ ...previous, price: event.target.value }))}
                    className="bg-input-background border-border text-foreground w-28"
                  />
                  <Button type="button" onClick={addAddonOption} variant="outline" className="bg-card hover:bg-accent border-border text-foreground">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {addons.length > 0 ? (
                  <div className="space-y-2">
                    {addons.map((addon, index) => (
                      <div
                        key={`${addon.name}-${index}`}
                        className="flex items-center justify-between p-3 bg-muted/50 rounded-lg border border-border"
                      >
                        <div>
                          <span className="text-foreground">{addon.name}</span>
                          <span className="text-emerald-600 dark:text-emerald-400 ml-3">
                            +${Number.parseFloat(addon.price).toFixed(2)}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeAddon(index)}
                          className="text-muted-foreground hover:text-destructive"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            </section>

            <section className="space-y-4">
              <div className="flex items-center gap-2 pb-2 border-b border-border">
                <Package className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-foreground">{addItemCopy.sections.stock}</h3>
              </div>
              <div className="space-y-2">
                <Label htmlFor="menu-item-stock" className="text-foreground">
                  {addItemCopy.labels.stockQuantity}
                </Label>
                <Input
                  id="menu-item-stock"
                  type="number"
                  placeholder={addItemCopy.placeholders.stockQuantity}
                  value={formData.stockQuantity}
                  onChange={(event) => setFormData((previous) => ({ ...previous, stockQuantity: event.target.value }))}
                  className="bg-input-background border-border text-foreground"
                />
                <p className="text-sm text-muted-foreground">{addItemCopy.helpers.stock}</p>
              </div>
            </section>

            <div className="flex justify-end gap-3 pt-4 border-t border-border">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  resetForm();
                  onOpenChange(false);
                }}
                className="bg-card hover:bg-accent border-border text-foreground"
                disabled={createItemMutation.isPending}
              >
                {addItemCopy.buttons.cancel}
              </Button>
              <Button
                type="submit"
                className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-500/25"
                disabled={createItemMutation.isPending || !tenantSlug}
              >
                {createItemMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
                {addItemCopy.buttons.submit}
              </Button>
            </div>
          </form>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

const MenuPageV3 = () => {
  const { activeRestaurantId, activeSubdomain, slug } = useTenantStore();
  const { slug: pathSlug } = useTenantPath();
  const tenantSlug =
    pathSlug ??
    activeSubdomain ??
    slug ??
    (typeof window !== 'undefined' ? localStorage.getItem('tenantSlug') ?? undefined : undefined);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation('menu');
  const { t: tCommon } = useTranslation('common');
  const pageCopy = useMenuPageCopy();

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive' | 'out-of-stock'>('all');
  const [selectedCategory, setSelectedCategory] = useState<'all' | string>('all');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isAddCategoryModalOpen, setIsAddCategoryModalOpen] = useState(false);
  const [categoryToEdit, setCategoryToEdit] = useState<MenuCategory | null>(null);
  const [isQuickEditOpen, setIsQuickEditOpen] = useState(false);
  const [quickEditItem, setQuickEditItem] = useState<MenuItem | null>(null);
  const [isQuickEditSaving, setIsQuickEditSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [pendingItemId, setPendingItemId] = useState<string | null>(null);
  const [isGroupManagerOpen, setIsGroupManagerOpen] = useState(false);
  const [groupManagerItemId, setGroupManagerItemId] = useState<string>('');
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const activeBulkJobStatuses = useMemo(
    () => new Set<MenuBulkJobRow['status']>(['pending', 'validating', 'running']),
    [],
  );

const menuQuery = useQuery({
  queryKey: ['menu', { tenant: tenantSlug, restaurantId: activeRestaurantId ?? null }],
  queryFn: async ({ signal }) => {
    try {
      if (!tenantSlug) {
          throw new Error('missing tenant slug');
        }
        const data = await fetchMenu(tenantSlug, { includeInactive: true, signal });
        if (!signal?.aborted) setLoadError(null);
        return data;
      } catch (error) {
        console.warn('[MenuPage] failed to load menu', error);
        if (!signal?.aborted) {
          setLoadError(t('errors.load'));
        }
        return { categories: [] as MenuCategory[] };
      }
    },
    enabled: Boolean(tenantSlug),
    staleTime: 30_000,
  });

  const menuTimezone = menuQuery.data?.timezone ?? null;

  const metricsQuery = useQuery({
    queryKey: ['menuMetrics', tenantSlug, METRICS_WINDOW_DAYS],
    queryFn: () => fetchMenuMetrics(tenantSlug!, { days: METRICS_WINDOW_DAYS }),
    enabled: Boolean(tenantSlug),
    staleTime: 60_000,
  });

  const topItemsQuery = useQuery({
    queryKey: ['menuTopItems', tenantSlug, METRICS_WINDOW_DAYS],
    queryFn: () => fetchMenuTopItems(tenantSlug!, { days: METRICS_WINDOW_DAYS }),
    enabled: Boolean(tenantSlug),
    staleTime: 60_000,
  });

  const bulkJobsQuery = useQuery({
    queryKey: ['menuBulkJobs', tenantSlug],
    queryFn: async () => {
      if (!tenantSlug) {
        return [] as MenuBulkJobRow[];
      }
      return fetchMenuBulkJobs(tenantSlug, { limit: 15 });
    },
    enabled: Boolean(tenantSlug),
    refetchInterval: (query) => {
      const jobs = query.state.data as MenuBulkJobRow[] | undefined;
      if (!jobs || jobs.length === 0) {
        return false;
      }
      return jobs.some((job) => activeBulkJobStatuses.has(job.status)) ? 5000 : false;
    },
  });

  const toggleMutation = useMutation({
    mutationFn: ({ itemId, isEightySixed }: { itemId: string; isEightySixed: boolean }) => {
      if (!tenantSlug) {
        return Promise.reject(new Error(t('toasts.tenantRequired.items')));
      }
      return toggleItemEightySix(tenantSlug, itemId, { isEightySixed });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['menu'] });
      queryClient.invalidateQueries({ queryKey: ['menuMetrics'] });
      queryClient.invalidateQueries({ queryKey: ['menuTopItems'] });
      toast({ title: t('toasts.menuUpdated.title'), description: t('toasts.menuUpdated.description') });
    },
    onError: (error: unknown) => {
      toast({
        title: t('toasts.toggleError.title'),
        description: error instanceof Error ? error.message : t('toasts.toggleError.description'),
        variant: 'destructive',
      });
    },
    onSettled: () => {
      setPendingItemId(null);
    },
  });

  const createItemsCsv = useCallback((data: MenuBulkData) => {
    const categoryMap = new Map<string, string>();
    data.categories.forEach((category) => {
      if (category.id) {
        categoryMap.set(category.id, category.name);
      }
    });
    const rows = data.items.map((item) => ({
      Category: categoryMap.get(item.categoryId) ?? item.categoryId,
      Item: item.name,
      Description: item.description ?? '',
      Price: (item.priceCents / 100).toFixed(2),
      Active: item.isActive ? 'true' : 'false',
      TaxBps: item.taxRateBps ?? '',
      Allergens: (item.allergenTags ?? []).join('; '),
    }));
    return Papa.unparse(rows, {
      fields: ['Category', 'Item', 'Description', 'Price', 'Active', 'TaxBps', 'Allergens'],
    });
  }, []);

  const exportMenuMutation = useMutation({
    mutationFn: async ({ format }: { format: 'json' | 'csv-items' }) => {
      if (!tenantSlug) {
        throw new Error(t('toasts.tenantRequired.items'));
      }
      const data = await exportMenuBulk(tenantSlug);
      const timestamp = new Date().toISOString();
      if (format === 'json') {
        const blob = new Blob([JSON.stringify(data, null, 2)], {
          type: 'application/json',
        });
        return { blob, fileName: `menu-export-${timestamp}.json`, format };
      }
      const csv = createItemsCsv(data);
      const blob = new Blob([csv], { type: 'text/csv' });
      return { blob, fileName: `menu-items-${timestamp}.csv`, format };
    },
    onSuccess: ({ blob, fileName, format }) => {
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);
      toast({
        title: format === 'json' ? 'JSON export ready' : 'CSV export ready',
        description: 'Download complete.',
      });
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Unable to export menu data.';
      toast({ title: 'Export failed', description: message, variant: 'destructive' });
    },
  });

  const exportPendingFormat = exportMenuMutation.variables?.format;

  const importMenuMutation = useMutation({
    mutationFn: async (payload: MenuBulkData | MenuBulkImportEnvelope) => {
      if (!tenantSlug) {
        throw new Error(t('toasts.tenantRequired.items'));
      }
      return importMenuBulk(payload, tenantSlug);
    },
    onSuccess: (result, variables) => {
      const format =
        variables && 'data' in variables ? variables.format ?? 'json' : 'json';
      toast({
        title: `${format.toUpperCase()} import started`,
        description: `Job ${result.jobId} is processing.`,
      });
      void bulkJobsQuery.refetch();
      setIsImportDialogOpen(false);
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Unable to import menu data.';
      toast({ title: 'Import failed', description: message, variant: 'destructive' });
    },
  });

  const handleOpenImportDialog = useCallback(() => {
    if (!tenantSlug) {
      toast({
        title: t('toasts.tenantRequired.title'),
        description: t('toasts.tenantRequired.items'),
        variant: 'destructive',
      });
      return;
    }
    setIsImportDialogOpen(true);
  }, [tenantSlug, toast, t]);

  const handleImportSubmit = useCallback(
    (payload: MenuBulkImportEnvelope) => importMenuMutation.mutateAsync(payload),
    [importMenuMutation],
  );

  const categories = useMemo(() => menuQuery.data?.categories ?? [], [menuQuery.data?.categories]);
  const bulkJobs = bulkJobsQuery.data ?? [];
  const bulkJobsError =
    bulkJobsQuery.isError
      ? bulkJobsQuery.error instanceof Error
        ? bulkJobsQuery.error.message
        : 'Unable to load bulk history'
      : null;

  const translationStrings = useMemo(
    () => ({
      justNow: tCommon('dates.justNow'),
      lastUpdatedTemplate: t('metadata.lastUpdated'),
    }),
    [t, tCommon],
  );

  const lastUpdatedLabel = useMemo(() => {
    const timeLabel = menuQuery.dataUpdatedAt
      ? new Date(menuQuery.dataUpdatedAt).toLocaleTimeString()
      : translationStrings.justNow;
    return translationStrings.lastUpdatedTemplate.replace('{{time}}', timeLabel);
  }, [menuQuery.dataUpdatedAt, translationStrings]);

  const orderedCategories = useMemo(
    () => [...categories].sort((a, b) => a.sortOrder - b.sortOrder),
    [categories],
  );

  const allMenuItems = useMemo(() => orderedCategories.flatMap((category) => category.items), [orderedCategories]);

  useEffect(() => {
    if (!allMenuItems.length) {
      if (groupManagerItemId !== '') {
        setGroupManagerItemId('');
      }
      return;
    }
    if (!groupManagerItemId || !allMenuItems.some((item) => item.id === groupManagerItemId)) {
      setGroupManagerItemId(allMenuItems[0]?.id ?? '');
    }
  }, [allMenuItems, groupManagerItemId]);

  const quickFilters = useMemo(() => {
    const entries = orderedCategories.map((category) => {
      const key = category.name.toLowerCase();
      const Icon = categoryIconMap[key] ?? (key.includes('spice') || key.includes('hot') ? Flame : Package);
      return {
        id: category.id,
        name: category.name,
        count: category.items.length,
        icon: Icon,
      };
    });

    const totalCount = entries.reduce((sum, entry) => sum + entry.count, 0);

    return [{ id: 'all', name: pageCopy.quickFilters.all, count: totalCount, icon: Package }, ...entries];
  }, [orderedCategories, pageCopy.quickFilters.all]);

  const metricsSummary = metricsQuery.data?.summary;

  const viewTrendValue = useMemo(() => {
    if (!metricsSummary?.views || metricsSummary.views.byDate.length < 2) return 0;
    const sorted = [...metricsSummary.views.byDate].sort((a, b) => a.date.localeCompare(b.date));
    const latest = sorted[sorted.length - 1]?.viewCount ?? 0;
    const previous = sorted[sorted.length - 2]?.viewCount ?? 0;
    return latest - previous;
  }, [metricsSummary]);

  const metricsCards = useMemo(() => {
    const counts = metricsSummary?.counts;
    const views = metricsSummary?.views;
    const conversions = metricsSummary?.conversions;

    const totalItems =
      counts?.totalItems ?? orderedCategories.reduce((acc, category) => acc + category.items.length, 0);
    const activeItems = orderedCategories.reduce(
      (acc, category) => acc + category.items.filter((item) => getItemStatus(item) === 'active').length,
      0,
    );

    const totalItemsTrend = totalItems
      ? pageCopy.analytics.totalItemsTrend(activeItems.toLocaleString())
      : pageCopy.analytics.totalItemsFallback;

    const mostViewedTrend = metricsSummary?.mostViewed
      ? pageCopy.analytics.mostViewedTrend(metricsSummary.mostViewed.viewCount.toLocaleString())
      : pageCopy.analytics.mostViewedFallback;

    const viewsTrend =
      viewTrendValue > 0
        ? pageCopy.analytics.viewsTrendPositive(viewTrendValue.toLocaleString())
        : viewTrendValue < 0
        ? pageCopy.analytics.viewsTrendNegative(viewTrendValue.toLocaleString())
        : pageCopy.analytics.viewsTrendFlat;

    const conversionsTrend = conversions
      ? pageCopy.analytics.conversionTrend(conversions.totalOrders.toLocaleString())
      : pageCopy.analytics.conversionFallback;

    return [
      {
        label: pageCopy.analytics.totalItemsLabel,
        value: totalItems ? totalItems.toLocaleString() : '--',
        trend: totalItemsTrend,
        trendUp: true,
        icon: Package,
        bgGradient: 'from-blue-500/10 to-blue-600/5',
        iconBg: 'bg-blue-500/10 border-blue-500/20',
        iconColor: 'text-blue-600 dark:text-blue-400',
      },
      {
        label: pageCopy.analytics.mostViewedLabel,
        value: metricsSummary?.mostViewed ? metricsSummary.mostViewed.name : pageCopy.analytics.mostViewedValueFallback,
        trend: mostViewedTrend,
        trendUp: Boolean(metricsSummary?.mostViewed),
        icon: Eye,
        bgGradient: 'from-emerald-500/10 to-emerald-600/5',
        iconBg: 'bg-emerald-500/10 border-emerald-500/20',
        iconColor: 'text-emerald-600 dark:text-emerald-400',
      },
      {
        label: pageCopy.analytics.viewsLabel,
        value: views ? views.total.toLocaleString() : '--',
        trend: viewsTrend,
        trendUp: viewTrendValue >= 0,
        icon: BarChart3,
        bgGradient: 'from-purple-500/10 to-purple-600/5',
        iconBg: 'bg-purple-500/10 border-purple-500/20',
        iconColor: 'text-purple-600 dark:text-purple-400',
      },
      {
        label: pageCopy.analytics.conversionLabel,
        value: conversions ? `${conversions.conversionRate.toFixed(1)}%` : '--',
        trend: conversionsTrend,
        trendUp: Boolean(conversions && conversions.conversionRate >= 50),
        icon: ArrowUpRight,
        bgGradient: 'from-orange-500/10 to-orange-600/5',
        iconBg: 'bg-orange-500/10 border-orange-500/20',
        iconColor: 'text-orange-600 dark:text-orange-400',
      },
    ];
  }, [metricsSummary, orderedCategories, pageCopy.analytics, viewTrendValue]);

  const topItemsMap = useMemo(() => {
    const collection = new Map<string, MenuAnalyticsTopItem>();
    topItemsQuery.data?.items.forEach((item) => {
      collection.set(item.id, item);
    });
    return collection;
  }, [topItemsQuery.data]);

  const filteredItems = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const items: Array<{ item: MenuItem; category: MenuCategory }> = [];

    orderedCategories.forEach((category) => {
      if (selectedCategory !== 'all' && category.id !== selectedCategory) {
        return;
      }

      const categoryItems = category.items
        .filter((menuItem) => {
          const status = getItemStatus(menuItem);
          if (statusFilter !== 'all' && status !== statusFilter) {
            return false;
          }

          if (!term) {
            return true;
          }

          const description = menuItem.description?.toLowerCase() ?? '';
          return (
            menuItem.name.toLowerCase().includes(term) ||
            description.includes(term) ||
            category.name.toLowerCase().includes(term)
          );
        })
        .sort((a, b) => a.sortOrder - b.sortOrder);

      categoryItems.forEach((menuItem) => {
        items.push({ item: menuItem, category });
      });
    });

    return items;
  }, [orderedCategories, selectedCategory, statusFilter, searchTerm]);

  const handleQuickEdit = useCallback((item: MenuItem) => {
    setQuickEditItem(item);
    setIsQuickEditOpen(true);
  }, []);

  const handleQuickEditSubmit = useCallback(
    async (payload: {
      name: string;
      description: string | null;
      priceCents: number;
      categoryId: string;
      isActive: boolean;
      imageUrl: string | null;
      availabilityRules: MenuAvailabilityRules;
      autoExpireAt: number | null;
      imageAltText: string | null;
      imageSpiceLevel: MenuSpiceLevel | null;
      soldOutOption: 'none' | '30' | '60' | '120' | 'custom';
      customSoldOutMinutes: string;
      translations: Record<string, { name?: string; description?: string; imageAltText?: string | null }>;
    }) => {
      if (!tenantSlug || !quickEditItem) return;
      setIsQuickEditSaving(true);
      try {
        await updateMenuItemDetails(tenantSlug, quickEditItem.id, {
          name: payload.name,
          description: payload.description,
          priceCents: payload.priceCents,
          categoryId: payload.categoryId,
          isActive: payload.isActive,
          imageUrl: payload.imageUrl,
          availabilityRules: payload.availabilityRules,
          autoExpireAt: payload.autoExpireAt,
          imageAltText: payload.imageAltText,
          imageSpiceLevel: payload.imageSpiceLevel,
          translations: payload.translations,
        });

        if (payload.soldOutOption !== 'none') {
          let duration: number | null = null;
          if (payload.soldOutOption === 'custom') {
            duration = Number.parseInt(payload.customSoldOutMinutes, 10);
            if (!Number.isFinite(duration) || duration <= 0) {
              toast({
                title: 'Invalid sold-out duration',
                description: 'Enter a duration greater than zero in minutes.',
                variant: 'destructive',
              });
              duration = null;
            }
          } else {
            duration = Number.parseInt(payload.soldOutOption, 10);
          }
          await toggleItemEightySix(tenantSlug, quickEditItem.id, {
            isEightySixed: true,
            soldOutDurationMinutes: duration ?? undefined,
            soldOutUntil: duration === null ? null : undefined,
          });
        } else {
          await toggleItemEightySix(tenantSlug, quickEditItem.id, {
            isEightySixed: !payload.isActive,
            soldOutUntil: null,
          });
        }

        queryClient.invalidateQueries({ queryKey: ['menu'] });
        queryClient.invalidateQueries({ queryKey: ['menuMetrics'] });
        toast({
          title: t('messages.itemUpdated.title', { defaultValue: 'Item updated' }),
          description: t('messages.itemUpdated.description', {
            name: payload.name,
            defaultValue: `${payload.name} was updated successfully`,
          }),
        });
        setIsQuickEditOpen(false);
        setQuickEditItem(null);
        recordMenuEvent(tenantSlug, {
          eventType: 'interaction',
          menuItemId: quickEditItem.id,
          categoryId: quickEditItem.categoryId ?? undefined,
        }).catch(() => null);
      } catch (error) {
        console.error('[MenuPage] quick edit failed', error);
        toast({
          title: t('messages.itemUpdateError.title', { defaultValue: 'Unable to update item' }),
          description:
            error instanceof Error && error.message
              ? error.message
              : t('messages.itemUpdateError.description', { defaultValue: 'Please try again.' }),
          variant: 'destructive',
        });
      } finally {
        setIsQuickEditSaving(false);
      }
    },
    [tenantSlug, quickEditItem, queryClient, t, toast],
  );

  const handleToggleAvailability = useCallback(
    (item: MenuItem) => {
      setPendingItemId(item.id);
      const currentlyEightySixed = item.availability?.status === 'temporarily_unavailable';
      toggleMutation.mutate({ itemId: item.id, isEightySixed: !currentlyEightySixed });
      if (tenantSlug) {
        recordMenuEvent(tenantSlug, {
          eventType: 'interaction',
          menuItemId: item.id,
          categoryId: item.categoryId ?? undefined,
        }).catch(() => null);
      }
    },
    [toggleMutation, tenantSlug],
  );

  const handleViewItem = useCallback(
    (item: MenuItem) => {
      if (!tenantSlug) {
        toast({
          title: t('toasts.tenantRequired.title'),
          description: pageCopy.messages.selectTenantToPreview,
          variant: 'destructive',
        });
        return;
      }

      recordMenuEvent(tenantSlug, {
        eventType: 'view',
        menuItemId: item.id,
        categoryId: item.categoryId ?? undefined,
      }).catch(() => null);

      if (typeof window !== 'undefined') {
        const basePath = activeSubdomain ? `/r/${activeSubdomain}/menu` : '/menu';
        const url = new URL(basePath, window.location.origin);
        url.searchParams.set('item', item.id);
        window.open(url.toString(), '_blank', 'noopener');
      }
    },
    [activeSubdomain, pageCopy.messages.selectTenantToPreview, tenantSlug, toast, t],
  );

  const handleDeleteItem = useCallback(
    async (item: MenuItem) => {
      if (!tenantSlug) {
        toast({
          title: t('toasts.tenantRequired.title'),
          description: t('toasts.tenantRequired.items'),
          variant: 'destructive',
        });
        return;
      }
      const confirmed = window.confirm(t('prompts.bulkDelete_one'));
      if (!confirmed) return;
      try {
        await deleteMenuItem(tenantSlug, item.id);
        const prune = (data: FetchMenuResponse | undefined): FetchMenuResponse | undefined => {
          if (!data) {
            return data;
          }
          const updatedCategories = data.categories.map((category) => ({
            ...category,
            items: category.items.filter((entry) => entry.id !== item.id),
          }));
          return { categories: updatedCategories };
        };
        queryClient.setQueriesData<FetchMenuResponse>({ queryKey: ['menu'] }, prune);
        queryClient.setQueriesData<FetchMenuResponse>({ queryKey: ['menu', 'admin'] }, prune);
        queryClient.invalidateQueries({ queryKey: ['menu'] });
        queryClient.invalidateQueries({ queryKey: ['menuMetrics'] });
        queryClient.invalidateQueries({ queryKey: ['menuTopItems'] });
        toast({
          title: t('toasts.itemsDeleted.title'),
          description: t('toasts.itemsDeleted.description_one'),
        });
      } catch (error) {
        console.error('[MenuPage] delete item failed', error);
        toast({
          title: t('toasts.deleteError.title'),
          description: error instanceof Error ? error.message : t('toasts.deleteError.description'),
          variant: 'destructive',
        });
      }
    },
    [tenantSlug, toast, t, queryClient],
  );


  const handleAddItemClick = useCallback(() => {
    if (!tenantSlug) {
      toast({
        title: t('toasts.tenantRequired.title'),
        description: t('toasts.tenantRequired.items'),
        variant: 'destructive',
      });
      return;
    }
    setIsAddDialogOpen(true);
  }, [tenantSlug, toast, t]);

  const handleOpenCategories = useCallback(() => {
    if (!tenantSlug) {
      toast({
        title: t('toasts.tenantRequired.title'),
        description: t('toasts.tenantRequired.categories'),
        variant: 'destructive',
      });
      return;
    }
    setCategoryToEdit(null);
    setIsAddCategoryModalOpen(true);
  }, [tenantSlug, toast, t]);

  const handleEditSelectedCategory = useCallback(() => {
    if (!tenantSlug) {
      toast({
        title: t('toasts.tenantRequired.title'),
        description: t('toasts.tenantRequired.categories'),
        variant: 'destructive',
      });
      return;
    }

    const targetCategoryId =
      selectedCategory === 'all' ? orderedCategories[0]?.id ?? null : selectedCategory;

    if (!targetCategoryId) {
      toast({
        title: t('toasts.categoryMissing.title', { defaultValue: 'Category not found' }),
        description: t('toasts.categoryMissing.description', { defaultValue: 'Please add a category before editing.' }),
        variant: 'destructive',
      });
      return;
    }

    if (selectedCategory === 'all') {
      setSelectedCategory(targetCategoryId);
    }

    const category = orderedCategories.find((entry) => entry.id === targetCategoryId);
    if (!category) {
      toast({
        title: t('toasts.categoryMissing.title', { defaultValue: 'Category not found' }),
        description: t('toasts.categoryMissing.description', { defaultValue: 'Please refresh and try again.' }),
        variant: 'destructive',
      });
      return;
    }
    setCategoryToEdit(category);
    setIsAddCategoryModalOpen(true);
  }, [orderedCategories, selectedCategory, setSelectedCategory, tenantSlug, toast, t]);

  const handleOpenGroupManager = useCallback(
    (itemId?: string) => {
      if (!tenantSlug) {
        toast({
          title: t('toasts.tenantRequired.title'),
          description: t('toasts.tenantRequired.items'),
          variant: 'destructive',
        });
        return;
      }
      if (!allMenuItems.length) {
        toast({
          title: t('toasts.managerUnavailable.title', {
            defaultValue: 'Add a menu item first',
          }),
          description: t('toasts.managerUnavailable.description', {
            defaultValue: 'Create a menu item before managing modifier groups.',
          }),
          variant: 'destructive',
        });
        return;
      }
      const preferredId = itemId && allMenuItems.some((entry) => entry.id === itemId)
        ? itemId
        : groupManagerItemId && allMenuItems.some((entry) => entry.id === groupManagerItemId)
          ? groupManagerItemId
          : allMenuItems[0]!.id;
      setGroupManagerItemId(preferredId);
      setIsGroupManagerOpen(true);
    },
    [tenantSlug, toast, t, allMenuItems, groupManagerItemId],
  );

  return (
    <div className="px-6 pt-1 pb-6">
      <div className="mx-auto min-h-screen max-w-[2000px] space-y-6 p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2.5 bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 rounded-xl border border-emerald-500/20">
                <Package className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <h1 className="text-foreground text-2xl font-semibold">{pageCopy.header.title}</h1>
                <p className="text-sm text-muted-foreground">{pageCopy.header.subtitle}</p>
              </div>
            </div>
            <p className="ml-[52px] text-xs text-muted-foreground">{lastUpdatedLabel}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenGroupManager()}
              className="bg-card hover:bg-accent border-border text-foreground"
              disabled={!tenantSlug || allMenuItems.length === 0}
            >
              <ListChecks className="h-4 w-4 mr-2" />
              Manage modifier groups
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleEditSelectedCategory}
              className="bg-card hover:bg-accent border-border text-foreground"
              disabled={!tenantSlug || orderedCategories.length === 0}
            >
              <Pencil className="h-4 w-4 mr-2" />
              {pageCopy.actions.editCategory}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleOpenCategories}
              className="bg-card hover:bg-accent border-border text-foreground"
              disabled={!tenantSlug}
            >
              <Plus className="h-4 w-4 mr-2" />
              {pageCopy.actions.addCategory}
            </Button>
            <Button
              onClick={handleAddItemClick}
              className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-500/25"
              disabled={!tenantSlug}
            >
              <Plus className="h-4 w-4 mr-2" />
              {pageCopy.actions.addMenuItem}
            </Button>
          </div>
        </div>

        {loadError ? (
          <Alert variant="destructive">
            <AlertDescription>{loadError}</AlertDescription>
          </Alert>
        ) : null}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {metricsCards.map((metric) => {
            const Icon = metric.icon;
            return (
              <Card
                key={metric.label}
                className="relative overflow-hidden border-border bg-card hover:shadow-lg transition-all duration-300 group"
              >
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${metric.bgGradient} opacity-50 group-hover:opacity-70 transition-opacity`}
                />
                <CardContent className="relative p-5 space-y-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">{metric.label}</p>
                      <p className="text-foreground text-xl font-semibold">{metric.value}</p>
                    </div>
                    <div className={`p-2 rounded-lg ${metric.iconBg}`}>
                      <Icon className={`h-4 w-4 ${metric.iconColor}`} />
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {metric.trendUp ? (
                      <TrendingUp className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
                    ) : (
                      <TrendingDown className="h-3.5 w-3.5 text-red-600 dark:text-red-400" />
                    )}
                    <span
                      className={`text-sm ${metric.trendUp ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}
                    >
                      {metric.trend}
                    </span>
                    <span className="text-sm text-muted-foreground">{pageCopy.analytics.today}</span>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <Card className="border-border bg-card">
          <CardHeader className="space-y-1">
            <CardTitle className="text-lg">Bulk import & export</CardTitle>
            <CardDescription>
              Sync menu data across tools with JSON exports or CSV mapping imports.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                onClick={() => exportMenuMutation.mutate({ format: 'json' })}
                disabled={!tenantSlug || exportMenuMutation.isPending}
              >
                {exportMenuMutation.isPending && exportPendingFormat === 'json' ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Download className="mr-2 h-4 w-4" />
                )}
                Download JSON
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => exportMenuMutation.mutate({ format: 'csv-items' })}
                disabled={!tenantSlug || exportMenuMutation.isPending}
              >
                {exportMenuMutation.isPending && exportPendingFormat === 'csv-items' ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <FileSpreadsheet className="mr-2 h-4 w-4" />
                )}
                Download CSV
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={handleOpenImportDialog}
                disabled={importMenuMutation.isPending || !tenantSlug}
              >
                {importMenuMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <UploadCloud className="mr-2 h-4 w-4" />
                )}
                Import data
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Saved mappings are stored per tenant. Use CSV to remap columns or JSON for full fidelity exports.
            </p>
          </CardContent>
        </Card>

        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
          {quickFilters.map((filter) => {
            const Icon = filter.icon;
            const isActive = selectedCategory === filter.id;
            return (
              <div key={filter.id} className="flex items-center gap-1">
                <button
                  onClick={() => setSelectedCategory(filter.id as typeof selectedCategory)}
                  className={cn(
                    'flex items-center gap-2 px-4 py-2.5 rounded-lg border transition-all duration-200 whitespace-nowrap',
                    isActive
                      ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-700 dark:text-emerald-400'
                      : 'bg-card border-border text-muted-foreground hover:bg-accent hover:text-foreground',
                  )}
                >
                  <Icon className="h-4 w-4" />
                  <span>{filter.name}</span>
                  <Badge
                    variant="secondary"
                    className={cn(
                      'ml-1',
                      isActive
                        ? 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 border-emerald-500/30'
                        : 'bg-muted text-muted-foreground',
                    )}
                  >
                    {filter.count}
                  </Badge>
                </button>
              </div>
            );
          })}
        </div>

        <Card className="border-border bg-card shadow-sm">
          <CardHeader className="border-b border-border">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <CardTitle className="text-foreground flex items-center gap-2">
                <ChefHat className="h-5 w-5 text-muted-foreground" />
                {pageCopy.directory.title}
              </CardTitle>
              <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                <div className="relative flex-1 sm:w-64">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                  <Input
                    placeholder={pageCopy.filters.searchPlaceholder}
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    className="pl-10 bg-input-background border-border text-foreground placeholder:text-muted-foreground"
                  />
                </div>
                <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as typeof statusFilter)}>
                  <SelectTrigger className="w-full sm:w-40 bg-input-background border-border text-foreground">
                    <Filter className="h-4 w-4 mr-2 text-muted-foreground" />
                    <SelectValue placeholder={pageCopy.filters.status.placeholder} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{pageCopy.filters.status.all}</SelectItem>
                    <SelectItem value="active">{pageCopy.filters.status.active}</SelectItem>
                    <SelectItem value="inactive">{pageCopy.filters.status.inactive}</SelectItem>
                    <SelectItem value="out-of-stock">{pageCopy.filters.status.outOfStock}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            {filteredItems.length > 0 ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {filteredItems.map(({ item, category }) => {
                  const status = getItemStatus(item);
                  const analytics = topItemsMap.get(item.id);
                  const orders = analytics?.orderCount ?? 0;
                  const viewCount = analytics?.viewCount ?? 0;
                  const ratingDisplay = analytics
                    ? (Math.min(4.9, 3.6 + Math.min(1.2, analytics.viewCount / 600))).toFixed(1)
                    : '-';
                  const price = formatCurrency(item.priceCents);
                  const prepTime = item.prepMinutes
                    ? pageCopy.labels.minutes(item.prepMinutes)
                    : item.description?.match(/\d+-\d+\s?min/i)?.[0] ?? '-';
                  const normalizedTags = item.tags ?? [];
                  const isVegetarian = normalizedTags.some((tag) => tag.toLowerCase().includes('vegetarian') || tag.toLowerCase().includes('vegan'));
                  const isSpicy = normalizedTags.some((tag) => tag.toLowerCase().includes('spicy'));
                  const isPopular = normalizedTags.some((tag) => tag.toLowerCase().includes('featured')) || viewCount > 200;
                  const availabilityToggleLabel =
                    status === 'out-of-stock'
                      ? pageCopy.actions.toggleAvailability.makeAvailable
                      : pageCopy.actions.toggleAvailability.makeUnavailable;

                  return (
                    <Card
                      key={item.id}
                      className="group hover:shadow-xl transition-all duration-300 border-border bg-card overflow-hidden"
                    >
                      <div className="flex gap-4 p-4">
                        <div className="relative flex-shrink-0 w-32 h-32 rounded-lg overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900 border border-border">
                          {item.imageUrl ? (
                            <img src={item.imageUrl} alt={item.imageAltText ?? item.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="absolute inset-0 flex items-center justify-center">
                              <ImageIcon className="h-12 w-12 text-muted-foreground/30" />
                            </div>
                          )}
                          {isPopular ? (
                            <div className="absolute top-2 left-2 bg-orange-500 text-white px-2 py-1 rounded-md text-xs flex items-center gap-1 shadow-lg z-10">
                              <Star className="h-3 w-3 fill-current" /> {pageCopy.badges.popular}
                            </div>
                          ) : null}
                          {status === 'out-of-stock' ? (
                            <div className="absolute inset-0 bg-gray-900/80 dark:bg-gray-950/90 flex items-center justify-center backdrop-blur-sm z-10">
                              <Badge variant="destructive" className="bg-red-600 text-white">
                                {pageCopy.badges.outOfStock}
                              </Badge>
                            </div>
                          ) : null}
                        </div>
                        <div className="flex-1 min-w-0 flex flex-col gap-4">
                          <div className="space-y-2">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <h3 className="text-foreground truncate mb-1 flex items-center gap-2">
                                  {item.name}
                                  {isVegetarian ? (
                                    <Leaf className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                                  ) : null}
                                  {isSpicy ? (
                                    <Flame className="h-3.5 w-3.5 text-orange-600 dark:text-orange-400 flex-shrink-0" />
                                  ) : null}
                                </h3>
                                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">{category.name}</p>
                                <p className="text-sm text-muted-foreground line-clamp-2">
                                  {item.description || pageCopy.directory.noDescription}
                                </p>
                              </div>
                              <Badge
                                variant={status === 'active' ? 'default' : 'secondary'}
                                className={cn(
                                  status === 'active'
                                    ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20'
                                    : status === 'out-of-stock'
                                    ? 'bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/20'
                                    : 'bg-muted text-muted-foreground border-border',
                                )}
                              >
                                {status === 'out-of-stock'
                                  ? pageCopy.badges.outShort
                                  : status === 'inactive'
                                  ? pageCopy.badges.hidden
                                  : pageCopy.badges.active}
                              </Badge>
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              {normalizedTags.slice(0, 3).map((tag) => (
                                <Badge
                                  key={`${item.id}-${tag}`}
                                  variant="outline"
                                  className="bg-muted text-xs text-muted-foreground"
                                >
                                  {tag}
                                </Badge>
                              ))}
                            </div>
                          </div>
                          <div className="grid grid-cols-4 gap-2">
                            <div className="bg-muted/50 rounded-lg p-2 border border-border">
                              <div className="flex items-center gap-1 mb-0.5">
                                <Star className="h-3 w-3 text-orange-500 fill-current" />
                                <span className="text-xs text-muted-foreground">{pageCopy.labels.rating}</span>
                              </div>
                              <p className="text-sm text-foreground">{ratingDisplay}</p>
                            </div>
                            <div className="bg-muted/50 rounded-lg p-2 border border-border">
                              <div className="flex items-center gap-1 mb-0.5">
                                <Users className="h-3 w-3 text-muted-foreground" />
                                <span className="text-xs text-muted-foreground">{pageCopy.labels.orders}</span>
                              </div>
                              <p className="text-sm text-foreground">{orders.toLocaleString()}</p>
                            </div>
                            <div className="bg-emerald-500/5 dark:bg-emerald-500/10 rounded-lg p-2 border border-emerald-500/20">
                              <div className="flex items-center gap-1 mb-0.5">
                                <DollarSign className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />
                                <span className="text-xs text-emerald-600 dark:text-emerald-400">{pageCopy.labels.price}</span>
                              </div>
                              <p className="text-sm text-emerald-700 dark:text-emerald-300">{price}</p>
                            </div>
                            <div className="bg-muted/50 rounded-lg p-2 border border-border">
                              <div className="flex items-center gap-1 mb-0.5">
                                <Clock className="h-3 w-3 text-muted-foreground" />
                                <span className="text-xs text-muted-foreground">{pageCopy.labels.time}</span>
                              </div>
                              <p className="text-xs text-foreground">{prepTime}</p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleQuickEdit(item)}
                              className="flex-1 bg-card hover:bg-accent border-border text-foreground"
                            >
                              <Pencil className="h-3.5 w-3.5 mr-1.5" />
                              {pageCopy.actions.edit}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleViewItem(item)}
                              className="flex-1 bg-card hover:bg-accent border-border text-foreground"
                            >
                              <Eye className="h-3.5 w-3.5 mr-1.5" />
                              {pageCopy.actions.view}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleToggleAvailability(item)}
                              className={cn(
                                status === 'active'
                                  ? 'bg-card hover:bg-accent border-border text-foreground'
                                  : 'bg-emerald-500/10 hover:bg-emerald-500/20 border-emerald-500/20 text-emerald-700 dark:text-emerald-400',
                              )}
                              disabled={toggleMutation.isPending && pendingItemId === item.id}
                              aria-label={availabilityToggleLabel}
                              title={availabilityToggleLabel}
                              aria-pressed={status !== 'out-of-stock'}
                            >
                              {status === 'out-of-stock' ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
                            </Button>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleDeleteItem(item)}
                            className="self-start text-xs text-muted-foreground hover:text-destructive"
                          >
                            {pageCopy.actions.removeItem}
                          </button>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-16 px-4">
                <div className="p-4 bg-muted/50 rounded-full mb-4">
                  <AlertCircle className="h-8 w-8 text-muted-foreground" />
                </div>
                <h3 className="text-foreground mb-2">{pageCopy.directory.empty.title}</h3>
                <p className="text-muted-foreground text-center mb-6 max-w-md">
                  {searchTerm || statusFilter !== 'all'
                    ? pageCopy.directory.empty.search
                    : pageCopy.directory.empty.getStarted}
                </p>
                {!searchTerm && statusFilter === 'all' ? (
                  <Button
                    onClick={handleAddItemClick}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-500/25"
                    disabled={!tenantSlug}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    {pageCopy.actions.addFirstMenuItem}
                  </Button>
                ) : null}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <BulkJobHistory
        jobs={bulkJobs}
        isLoading={bulkJobsQuery.isPending}
        isRefreshing={bulkJobsQuery.isFetching && !bulkJobsQuery.isPending}
        error={bulkJobsError}
        onRetry={() => {
          void bulkJobsQuery.refetch();
        }}
        activeStatuses={activeBulkJobStatuses}
      />

      <ModifierGroupsManagerDialog
        open={isGroupManagerOpen}
        onOpenChange={setIsGroupManagerOpen}
        tenantSlug={tenantSlug}
        items={allMenuItems}
        itemId={groupManagerItemId}
        onSelectItem={setGroupManagerItemId}
      />

      <QuickEditDialog
        open={isQuickEditOpen}
        item={quickEditItem}
        categories={orderedCategories}
        tenantSlug={tenantSlug}
        timezone={menuTimezone}
        onOpenManager={handleOpenGroupManager}
        onClose={() => {
          if (!isQuickEditSaving) {
            setIsQuickEditOpen(false);
            setQuickEditItem(null);
          }
        }}
        onSubmit={handleQuickEditSubmit}
        isSubmitting={isQuickEditSaving}
      />

      <AddMenuItemDialog
        open={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}
        tenantSlug={tenantSlug}
        categories={orderedCategories}
        defaultCategoryId={selectedCategory === 'all' ? orderedCategories[0]?.id : selectedCategory}
        timezone={menuTimezone}
      />

      <AddCategoryModal
        open={isAddCategoryModalOpen}
        onOpenChange={(open) => {
          setIsAddCategoryModalOpen(open);
          if (!open) {
            setCategoryToEdit(null);
          }
        }}
        tenantSlug={tenantSlug}
        timezone={menuTimezone}
        existingCategories={orderedCategories.map((category) => ({ id: category.id, name: category.name }))}
        initialCategory={categoryToEdit}
      />

      <BulkImportDialog
        isOpen={isImportDialogOpen}
        onOpenChange={(open) => {
          if (!open && importMenuMutation.isPending) {
            return;
          }
          setIsImportDialogOpen(open);
        }}
        onSubmit={handleImportSubmit}
        isSubmitting={importMenuMutation.isPending}
        tenantSlug={tenantSlug}
      />
    </div>
  );
};

export default MenuPageV3;
